import { supabase, userAPI } from './supabaseClient';
import {
  PlayerCharacter,
  CharacterFullView,
  GachaDrawResult,
  UserGameProfile,
  CharacterRace,
  CharacterColor,
  Faction,
  DailyClaimStatus,
  SugarClaimResult,
  SugarClaimHistory,
  AllocatedStats,
  StatAllocationResult,
  CheckCanChangeActiveResult,
  SetActiveCharacterResult,
  ToggleFavoriteResult,
  ItemCategory,
  Item,
  ItemFullView,
  PlayerItem,
  LimitedItem,
  CharacterEquipment,
  EquippedItemDetails,
  EquipItemResult,
  UnequipItemResult,
  EquipmentSlotType,
  Skill,
  CharacterSkillsFullData,
  SelectAttackModeResult,
  ShopVisitStatus,
  MarkShopVisitedResult,
  MiningResult,
  EnergyInfo,
  MiningStatistics,
  GetAvailableMiningResourcesResult,
  MiningResource,
  MiningMap,
  MiningNode,
  NodeDetails,
  MiningSession,
  MiningSessionWithDetails,
  CharacterMiningStats,
  StartMiningSessionResult,
  ProcessMiningTickResult,
  HarvestResult,
  CancelMiningSessionResult,
  ActiveMinerInfo,
  NodeMinerInfo,
  CharismaBonus,
  SynthesisCostBreakdown,
  SynthesisValidationResult,
  MiningFirstDigClaim,
  FirstDigEligibility,
  GetSynthesisMilestonesResult,
  CharacterSynthesisResult,
  GetSynthesizablePairsResult,
  GetSynthesisHistoryResult,
  PlayerSkinWithDetails,
  AvailableSkin,
  GetPlayerSkinsResult,
  GetAvailableSkinsResult,
  ChangeCharacterLayerResult,
  PurchaseSkinResult,
  LayerType,
  GetShopCharacterAssetsRequest,
  GetShopCharacterAssetsResult,
  ShopCharacterAsset,
  GetMaterialCharactersSkinsResult,
  MaterialCharacterSkin,
  LeaderboardEntry,
  GetLeaderboardResult,
  CombatPowerDetailResult,
  CombatPower,
  MarketplaceListing,
  GetMarketplaceListingsRequest,
  GetMarketplaceListingsResult,
  GetMyListingsRequest,
  GetMyListingsResult,
  ListItemRequest,
  ListItemResult,
  PurchaseItemRequest,
  PurchaseItemResult,
  CancelListingRequest,
  CancelListingResult,
  ValidateListingResult,
  ValidatePurchaseResult,
  MarketplacePriceLimit,
  MarketplaceConfig,
  ThroneStatus,
  ThroneChallengeResponse,
  ThroneAbdicationResult,
  ThroneBattleHistoryEntry,
  ThroneDailyRankingResult,
  ThroneRewardCheckResult,
  ThroneRewardClaimDetails,
  ItemRewardInfo,
  BattleStrategy,
  ValidationResult,
  SaveStrategyResponse,
  ExpiredHarvestSession,
  ClaimExpiredSessionResult,
  CancelExpiredSessionResult,
  StoryChapterData,
  StoryChapterCatalogItem,
  PlayerStoryProgress,
  AdvanceStorySceneResult,
  SubmitPuzzleAnswerResult,
  ClaimStoryChapterRewardsResult,
  SubmitTrialAttemptResult,
  SubmitMinigameResultResult
} from '../types/game';

type InGameWalletLedgerResult = {
  ledger_entry_id?: string;
};

function normalizeInGameWalletLedgerResult<T>(
  data: T,
  options: { preserveOnChainHash?: boolean } = {},
): T & InGameWalletLedgerResult & { tx_hash?: string } {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return data as T & InGameWalletLedgerResult;
  }

  const record = data as Record<string, unknown>;
  const {
    ledger_entry_id: canonicalLedgerEntryId,
    transaction_id: legacyTransactionId,
    transaction_hash: legacyTransactionHash,
    wallet_transaction_id: legacyWalletTransactionId,
    wallet_tx_id: legacyWalletTxId,
    ...result
  } = record;
  const ledgerEntryId = typeof canonicalLedgerEntryId === 'string'
    ? canonicalLedgerEntryId
    : typeof legacyTransactionId === 'string'
      ? legacyTransactionId
      : typeof legacyWalletTransactionId === 'string'
        ? legacyWalletTransactionId
        : typeof legacyWalletTxId === 'string'
          ? legacyWalletTxId
          : typeof legacyTransactionHash === 'string' && !/^0x[a-fA-F0-9]{64}$/.test(legacyTransactionHash)
            ? legacyTransactionHash
        : undefined;

  return {
    ...result,
    ...(ledgerEntryId ? { ledger_entry_id: ledgerEntryId } : {}),
    ...(options.preserveOnChainHash && typeof legacyTransactionHash === 'string' && /^0x[a-fA-F0-9]{64}$/.test(legacyTransactionHash)
      ? { tx_hash: legacyTransactionHash }
      : {}),
  } as T & InGameWalletLedgerResult;
}

export const gameAPI = {
  async drawCharacter(
    pricingTierId?: string
  ): Promise<(GachaDrawResult & InGameWalletLedgerResult) | null> {
    try {
      console.log('🎲 [gameAPI] Calling gacha_draw_character RPC...', {
        pricingTierId
      });

      const { data, error } = await supabase.rpc('gacha_draw_character', {
        p_pricing_tier_id: pricingTierId || null
      });

      if (error) {
        console.error('❌ [gameAPI] Error drawing character:', error);
        throw error;
      }

      if (!data) {
        console.error('❌ [gameAPI] No data returned from gacha_draw_character');
        return null;
      }

      // 檢查是否返回錯誤
      const normalizedData = normalizeInGameWalletLedgerResult(data);

      if (normalizedData.success === false) {
        console.error('❌ [gameAPI] Gacha draw failed:', normalizedData.error);
        throw new Error(normalizedData.error || '抽卡失敗');
      }

      console.log('✅ [gameAPI] Raw data from database:', JSON.stringify(normalizedData, null, 2));

      let factionName: string | undefined;
      if (normalizedData.faction_assigned && normalizedData.faction_id) {
        const factionData = await this.getFaction(normalizedData.faction_id);
        factionName = factionData?.name;
      }

      // 確保正確提取 color 和 race 資料
      const colorData = normalizedData.color || {};
      const raceData = normalizedData.race || {};

      console.log('🎨 [gameAPI] Color data:', colorData);
      console.log('🌟 [gameAPI] Race data:', raceData);

      const result: GachaDrawResult & InGameWalletLedgerResult = {
        character_id: normalizedData.character_id,
        color_id: colorData.id || '',
        race_id: raceData.id || '',
        color_name: colorData.name || 'bubble',
        color_display_name: colorData.display_name || '泡泡',
        race_name: raceData.name || 'SIRIANS',
        race_display_name: raceData.display_name || '天狼星人',
        final_stats: data.final_stats || {
          power: 0,
          defense: 0,
          agility: 0,
          charisma: 0,
          luck: 0,
          sweetness: 0
        },
        is_first_draw: normalizedData.is_first_draw || false,
        draw_sequence: normalizedData.draw_sequence || 1,
        assigned_faction_id: normalizedData.faction_id,
        assigned_faction_name: factionName,
        ledger_entry_id: normalizedData.ledger_entry_id,
      };

      console.log('✅ [gameAPI] Character drawn successfully, mapped result:', result);

      return result;
    } catch (error) {
      console.error('❌ [gameAPI] drawCharacter failed:', error);
      throw error;
    }
  },

  async getPlayerCharacters(limit = 100): Promise<CharacterFullView[]> {
    try {
      console.log('📋 [gameAPI] Fetching player characters...');

      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        console.error('❌ [gameAPI] No authenticated user');
        return [];
      }

      const { data, error } = await supabase.rpc('get_player_characters', {
        p_user_id: user.id,
        p_color_filter: null,
        p_race_filter: null,
        p_level_min: null,
        p_level_max: null,
        p_usable_only: null,
        p_sort_by: 'created_at',
        p_sort_order: 'DESC',
        p_limit: limit,
        p_offset: 0
      });

      if (error) {
        console.error('❌ [gameAPI] Error fetching characters:', error);
        throw error;
      }

      console.log('✅ [gameAPI] Characters fetched:', data?.length || 0);
      return (data || []) as CharacterFullView[];
    } catch (error) {
      console.error('❌ [gameAPI] getPlayerCharacters failed:', error);
      throw error;
    }
  },

  async getActiveCharacter(): Promise<CharacterFullView | null> {
    try {
      console.log('🎯 [gameAPI] Fetching active character...');

      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        console.error('❌ [gameAPI] No authenticated user');
        return null;
      }

      const { data, error } = await supabase
        .schema('bubble_game').from('character_full_view')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .maybeSingle();

      if (error) {
        console.error('❌ [gameAPI] Error fetching active character:', error);
        throw error;
      }

      if (data) {
        console.log('✅ [gameAPI] Active character found:', data.character_id);
        return data as CharacterFullView;
      }

      const characters = await this.getPlayerCharacters();

      if (characters.length === 0) {
        console.log('ℹ️ [gameAPI] No characters found');
        return null;
      }

      console.log('ℹ️ [gameAPI] No active character found, using most recent character');
      return characters[0];
    } catch (error) {
      console.error('❌ [gameAPI] getActiveCharacter failed:', error);
      throw error;
    }
  },

  async getUserProfile(): Promise<UserGameProfile | null> {
    try {
      console.log('👤 [gameAPI] Fetching user game profile...');

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.error('❌ [gameAPI] No authenticated user');
        return null;
      }

      const profile = await userAPI.getProfile();
      if (!profile) {
        console.log('ℹ️ [gameAPI] No user profile found');
        return null;
      }

      const data: UserGameProfile = {
        faction_id: profile.faction_id ?? null,
        skill_points: profile.skill_points ?? 0,
        daily_skill_points_amount: profile.daily_skill_points_amount ?? 0,
        last_skill_points_reset: profile.last_skill_points_reset ?? null,
      };
      console.log('✅ [gameAPI] User profile fetched through player profile contract');
      return data;
    } catch (error) {
      console.error('❌ [gameAPI] getUserProfile failed:', error);
      throw error;
    }
  },

  async getFaction(factionId: string): Promise<Faction | null> {
    try {
      console.log('🏛️ [gameAPI] Fetching faction:', factionId);

      const { data, error } = await supabase
        .schema('bubble_game').from('factions')
        .select('*')
        .eq('id', factionId)
        .maybeSingle();

      if (error) {
        console.error('❌ [gameAPI] Error fetching faction:', error);
        throw error;
      }

      if (!data) {
        console.log('ℹ️ [gameAPI] Faction not found');
        return null;
      }

      console.log('✅ [gameAPI] Faction fetched:', data.name);
      return data as Faction;
    } catch (error) {
      console.error('❌ [gameAPI] getFaction failed:', error);
      throw error;
    }
  },

  async getAllRaces(): Promise<CharacterRace[]> {
    try {
      console.log('🌟 [gameAPI] Fetching all races...');

      const { data, error } = await supabase
        .schema('bubble_game').from('character_races')
        .select('*')
        .order('probability', { ascending: false });

      if (error) {
        console.error('❌ [gameAPI] Error fetching races:', error);
        throw error;
      }

      console.log('✅ [gameAPI] Races fetched:', data?.length || 0);
      return (data || []) as CharacterRace[];
    } catch (error) {
      console.error('❌ [gameAPI] getAllRaces failed:', error);
      throw error;
    }
  },

  async getAllColors(): Promise<CharacterColor[]> {
    try {
      console.log('🎨 [gameAPI] Fetching all colors...');

      const { data, error } = await supabase
        .schema('bubble_game').from('character_colors')
        .select('*')
        .order('probability', { ascending: false });

      if (error) {
        console.error('❌ [gameAPI] Error fetching colors:', error);
        throw error;
      }

      console.log('✅ [gameAPI] Colors fetched:', data?.length || 0);
      return (data || []) as CharacterColor[];
    } catch (error) {
      console.error('❌ [gameAPI] getAllColors failed:', error);
      throw error;
    }
  },

  async checkDailyClaimStatus(): Promise<DailyClaimStatus> {
    try {
      console.log('🍬 [gameAPI] Checking daily candy claim status...');

      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        console.error('❌ [gameAPI] No authenticated user');
        return {
          success: false,
          can_claim: false,
          today_utc: '',
          last_claimed_date: null,
          claimable_amount: 0,
          base_amount: 50,
          milestone_bonus: 0,
          error: 'User not authenticated'
        };
      }

      const { data, error } = await supabase.rpc('check_daily_claim_status', {
        p_user_id: user.id
      });

      if (error) {
        console.error('❌ [gameAPI] Error checking claim status:', error);
        throw error;
      }

      console.log('✅ [gameAPI] Claim status:', data);
      return data as DailyClaimStatus;
    } catch (error) {
      console.error('❌ [gameAPI] checkDailyClaimStatus failed:', error);
      throw error;
    }
  },

  async claimDailySugarCubes(): Promise<SugarClaimResult> {
    try {
      console.log('🍬 [gameAPI] Claiming daily candy...');

      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        console.error('❌ [gameAPI] No authenticated user');
        return {
          success: false,
          message: 'User not authenticated',
          error: 'User not authenticated'
        };
      }

      const { data, error } = await supabase.rpc('claim_daily_sugar_cubes', {
        p_user_id: user.id
      });

      if (error) {
        console.error('❌ [gameAPI] Error claiming candy:', error);
        throw error;
      }

      console.log('✅ [gameAPI] Claim result:', data);
      return data as SugarClaimResult;
    } catch (error) {
      console.error('❌ [gameAPI] claimDailySugarCubes failed:', error);
      throw error;
    }
  },

  async getSugarClaimHistory(days: number = 30): Promise<SugarClaimHistory> {
    try {
      console.log('📜 [gameAPI] Fetching candy claim history...');

      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        console.error('❌ [gameAPI] No authenticated user');
        return {
          success: false,
          history: [],
          statistics: {
            total_claims: 0,
            total_claimed: 0,
            last_claim_date: null
          },
          error: 'User not authenticated'
        };
      }

      const { data, error } = await supabase.rpc('get_sugar_claim_history', {
        p_user_id: user.id,
        p_days: days,
        p_limit: 100
      });

      if (error) {
        console.error('❌ [gameAPI] Error fetching claim history:', error);
        throw error;
      }

      console.log('✅ [gameAPI] Claim history fetched:', data);
      return data as SugarClaimHistory;
    } catch (error) {
      console.error('❌ [gameAPI] getSugarClaimHistory failed:', error);
      throw error;
    }
  },

  async allocateCharacterStats(
    characterId: string,
    allocatedStats: AllocatedStats,
    bubbleAllocation: number = 0
  ): Promise<StatAllocationResult> {
    try {
      console.log('⚡ [gameAPI] Allocating character stats:', characterId, 'bubble:', bubbleAllocation);

      const { data, error } = await supabase.rpc('allocate_character_stats', {
        p_character_id: characterId,
        p_allocated_stats: allocatedStats,
        p_bubble_allocation: bubbleAllocation
      });

      if (error) {
        console.error('❌ [gameAPI] Error allocating stats:', error);
        return {
          success: false,
          error: error.message
        };
      }

      console.log('✅ [gameAPI] Stats allocated successfully:', data);
      return data as StatAllocationResult;
    } catch (error: any) {
      console.error('❌ [gameAPI] allocateCharacterStats failed:', error);
      return {
        success: false,
        error: error?.message || 'Unknown error occurred'
      };
    }
  },

  async getCharacterWithAllocation(characterId: string): Promise<PlayerCharacter | null> {
    try {
      console.log('🔍 [gameAPI] Fetching character with allocation:', characterId);

      const { data, error } = await supabase
        .schema('bubble_game').from('player_characters')
        .select(`
          *,
          color:character_colors(*),
          race:character_races(*)
        `)
        .eq('id', characterId)
        .maybeSingle();

      if (error) {
        console.error('❌ [gameAPI] Error fetching character:', error);
        throw error;
      }

      if (!data) {
        console.log('ℹ️ [gameAPI] Character not found');
        return null;
      }

      console.log('✅ [gameAPI] Character fetched:', data);
      return data as PlayerCharacter;
    } catch (error) {
      console.error('❌ [gameAPI] getCharacterWithAllocation failed:', error);
      throw error;
    }
  },

  async applyDailyBubbleDecay(characterId?: string): Promise<{
    success: boolean;
    affected_characters?: number;
    message?: string;
    error?: string;
  }> {
    try {
      console.log('🍭 [gameAPI] Applying daily bubble decay...', characterId || 'all characters');

      const { data, error } = await supabase.rpc('apply_daily_bubble_decay', {
        p_character_id: characterId || null
      });

      if (error) {
        console.error('❌ [gameAPI] Error applying bubble decay:', error);
        return {
          success: false,
          error: error.message
        };
      }

      console.log('✅ [gameAPI] Bubble decay applied:', data);
      return data as {
        success: boolean;
        affected_characters: number;
        message: string;
      };
    } catch (error: any) {
      console.error('❌ [gameAPI] applyDailyBubbleDecay failed:', error);
      return {
        success: false,
        error: error?.message || 'Unknown error occurred'
      };
    }
  },

  async checkCanChangeActive(): Promise<CheckCanChangeActiveResult> {
    try {
      console.log('🔄 [gameAPI] Checking if can change active character...');

      const { data, error } = await supabase.rpc('check_can_change_active');

      if (error) {
        console.error('❌ [gameAPI] Error checking change active status:', error);
        return {
          success: false,
          can_change: false,
          error: error.message
        };
      }

      console.log('✅ [gameAPI] Can change status:', data);
      return data as CheckCanChangeActiveResult;
    } catch (error: any) {
      console.error('❌ [gameAPI] checkCanChangeActive failed:', error);
      return {
        success: false,
        can_change: false,
        error: error?.message || 'Unknown error occurred'
      };
    }
  },

  async checkPrologueComplete(): Promise<boolean> {
    try {
      const { data, error } = await supabase.rpc('check_prologue_complete');
      if (error) return false;
      return data === true;
    } catch {
      return false;
    }
  },

  async setActiveCharacter(characterId: string): Promise<SetActiveCharacterResult> {
    try {
      console.log('🎯 [gameAPI] Setting active character:', characterId);

      const { data, error } = await supabase.rpc('set_active_character', {
        p_character_id: characterId
      });

      if (error) {
        console.error('❌ [gameAPI] Error setting active character:', error);
        return {
          success: false,
          error: error.message
        };
      }

      console.log('✅ [gameAPI] Active character set:', data);
      return data as SetActiveCharacterResult;
    } catch (error: any) {
      console.error('❌ [gameAPI] setActiveCharacter failed:', error);
      return {
        success: false,
        error: error?.message || 'Unknown error occurred'
      };
    }
  },

  async toggleFavoriteCharacter(characterId: string): Promise<ToggleFavoriteResult> {
    try {
      console.log('⭐ [gameAPI] Toggling favorite for character:', characterId);

      const { data, error } = await supabase.rpc('toggle_favorite_character', {
        p_character_id: characterId
      });

      if (error) {
        console.error('❌ [gameAPI] Error toggling favorite:', error);
        return {
          success: false,
          error: error.message
        };
      }

      console.log('✅ [gameAPI] Favorite toggled:', data);
      return data as ToggleFavoriteResult;
    } catch (error: any) {
      console.error('❌ [gameAPI] toggleFavoriteCharacter failed:', error);
      return {
        success: false,
        error: error?.message || 'Unknown error occurred'
      };
    }
  },

  // =============================================
  // 道具裝備系統 API
  // =============================================

  async getAllItemCategories(): Promise<ItemCategory[]> {
    try {
      console.log('📦 [gameAPI] Fetching all item categories...');

      const { data, error } = await supabase
        .schema('bubble_game').from('item_categories')
        .select('*')
        .order('name');

      if (error) {
        console.error('❌ [gameAPI] Error fetching item categories:', error);
        throw error;
      }

      console.log('✅ [gameAPI] Item categories fetched:', data?.length || 0);
      return (data || []) as ItemCategory[];
    } catch (error) {
      console.error('❌ [gameAPI] getAllItemCategories failed:', error);
      throw error;
    }
  },

  async getUserItems(): Promise<PlayerItem[]> {
    try {
      console.log('🎒 [gameAPI] Fetching user items...');

      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        console.error('❌ [gameAPI] No authenticated user');
        return [];
      }

      const { data: itemsData, error: itemsError } = await supabase
        .schema('bubble_game').from('player_items')
        .select(`
          *,
          item:items(
            *,
            category:item_categories(*)
          )
        `)
        .eq('user_id', user.id)
        .order('obtained_at', { ascending: false });

      if (itemsError) {
        console.error('❌ [gameAPI] Error fetching user items:', itemsError);
        throw itemsError;
      }

      const { data: equipmentData, error: equipmentError } = await supabase
        .schema('bubble_game').from('character_equipment')
        .select('player_item_id, character_id');

      if (equipmentError) {
        console.error('❌ [gameAPI] Error fetching equipment data:', equipmentError);
        throw equipmentError;
      }

      const equipmentCounts = (equipmentData || []).reduce((acc, eq) => {
        acc[eq.player_item_id] = (acc[eq.player_item_id] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const itemsWithCounts = (itemsData || []).map(item => ({
        ...item,
        equipped_count: equipmentCounts[item.id] || 0,
        available_count: item.quantity - (equipmentCounts[item.id] || 0)
      })) as PlayerItem[];

      console.log('✅ [gameAPI] User items fetched with equipment counts:', itemsWithCounts.length);
      return itemsWithCounts;
    } catch (error) {
      console.error('❌ [gameAPI] getUserItems failed:', error);
      throw error;
    }
  },

  async getItemsByCategory(categoryId?: string): Promise<PlayerItem[]> {
    try {
      console.log('📦 [gameAPI] Fetching items by category:', categoryId || 'all');

      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        console.error('❌ [gameAPI] No authenticated user');
        return [];
      }

      let query = supabase
        .schema('bubble_game').from('player_items')
        .select(`
          *,
          item:items(
            *,
            category:item_categories(*)
          )
        `)
        .eq('user_id', user.id);

      if (categoryId) {
        query = query.eq('item.category_id', categoryId);
      }

      const { data, error } = await query.order('obtained_at', { ascending: false });

      if (error) {
        console.error('❌ [gameAPI] Error fetching items by category:', error);
        throw error;
      }

      console.log('✅ [gameAPI] Items by category fetched:', data?.length || 0);
      return (data || []) as PlayerItem[];
    } catch (error) {
      console.error('❌ [gameAPI] getItemsByCategory failed:', error);
      throw error;
    }
  },

  async getActiveLimitedItems(): Promise<LimitedItem[]> {
    try {
      console.log('🎁 [gameAPI] Fetching active limited items');

      const { data, error } = await supabase.rpc('get_active_limited_items');

      if (error) {
        console.error('❌ [gameAPI] Failed to fetch limited items:', error);
        throw error;
      }

      console.log(`✅ [gameAPI] Fetched ${data?.length || 0} active limited items`);
      return (data || []) as LimitedItem[];
    } catch (error) {
      console.error('❌ [gameAPI] getActiveLimitedItems failed:', error);
      throw error;
    }
  },

  async getCharacterEquipment(characterId: string): Promise<EquippedItemDetails[]> {
    try {
      console.log('⚔️ [gameAPI] Fetching character equipment:', characterId);

      const { data, error } = await supabase
        .schema('bubble_game').from('character_equipment')
        .select(`
          *,
          player_item:player_items(
            *,
            item:items(
              *,
              category:item_categories(*)
            )
          )
        `)
        .eq('character_id', characterId);

      if (error) {
        console.error('❌ [gameAPI] Error fetching character equipment:', error);
        throw error;
      }

      console.log('✅ [gameAPI] Character equipment fetched:', data?.length || 0);

      // 轉換為 EquippedItemDetails 格式
      const equipment = (data || []).map(eq => ({
        ...eq,
        item_name: eq.player_item?.item?.name || '',
        item_display_name: eq.player_item?.item?.display_name || '',
        item_image_url: eq.player_item?.item?.image_url,
        item_rarity: eq.player_item?.item?.rarity || 'common',
        item_stat_bonuses: eq.player_item?.item?.stat_bonuses || {}
      })) as EquippedItemDetails[];

      return equipment;
    } catch (error) {
      console.error('❌ [gameAPI] getCharacterEquipment failed:', error);
      throw error;
    }
  },

  async getAllItems(): Promise<ItemFullView[]> {
    try {
      const { data, error } = await supabase
        .schema('bubble_game').from('items_full_view')
        .select('*')
        .order('rarity', { ascending: false })
        .order('display_name');

      if (error) {
        console.error('❌ [gameAPI] Error fetching all items:', error);
        throw error;
      }

      return (data || []) as ItemFullView[];
    } catch (error) {
      console.error('❌ [gameAPI] getAllItems failed:', error);
      throw error;
    }
  },

  async equipItemToCharacter(
    characterId: string,
    playerItemId: string,
    slotType: EquipmentSlotType
  ): Promise<EquipItemResult> {
    try {
      console.log('⚔️ [gameAPI] Equipping item to character:', { characterId, playerItemId, slotType });

      const { data, error } = await supabase.rpc('equip_item_to_character', {
        p_character_id: characterId,
        p_player_item_id: playerItemId,
        p_slot_type: slotType
      });

      if (error) {
        console.error('❌ [gameAPI] Error equipping item:', error);
        return {
          success: false,
          error: error.message
        };
      }

      console.log('✅ [gameAPI] Item equipped successfully:', data);
      return data as EquipItemResult;
    } catch (error: any) {
      console.error('❌ [gameAPI] equipItemToCharacter failed:', error);
      return {
        success: false,
        error: error?.message || 'Unknown error occurred'
      };
    }
  },

  async unequipItemFromCharacter(
    characterId: string,
    slotType: EquipmentSlotType
  ): Promise<UnequipItemResult> {
    try {
      console.log('🔓 [gameAPI] Unequipping item from character:', { characterId, slotType });

      const { data, error } = await supabase.rpc('unequip_item_from_character', {
        p_character_id: characterId,
        p_slot_type: slotType
      });

      if (error) {
        console.error('❌ [gameAPI] Error unequipping item:', error);
        return {
          success: false,
          error: error.message
        };
      }

      console.log('✅ [gameAPI] Item unequipped successfully:', data);
      return data as UnequipItemResult;
    } catch (error: any) {
      console.error('❌ [gameAPI] unequipItemFromCharacter failed:', error);
      return {
        success: false,
        error: error?.message || 'Unknown error occurred'
      };
    }
  },

  async getCharacterEquipmentWithDetails(characterId: string): Promise<EquippedItemDetails[]> {
    try {
      console.log('🎒 [gameAPI] Fetching character equipment with details:', characterId);

      const { data, error } = await supabase
        .schema('bubble_game').from('character_equipment')
        .select(`
          *,
          player_item:player_items!inner(
            *,
            item:items!inner(
              *,
              category:item_categories(*)
            )
          )
        `)
        .eq('character_id', characterId);

      if (error) {
        console.error('❌ [gameAPI] Error fetching equipment details:', error);
        throw error;
      }

      console.log('✅ [gameAPI] Equipment details fetched:', data?.length || 0);

      const equipment = (data || []).map(eq => ({
        ...eq,
        item_name: eq.player_item?.item?.name || '',
        item_display_name: eq.player_item?.item?.display_name || '',
        item_image_url: eq.player_item?.item?.image_url,
        item_rarity: eq.player_item?.item?.rarity || 'common',
        item_stat_bonuses: eq.player_item?.item?.stat_bonuses || {}
      })) as EquippedItemDetails[];

      return equipment;
    } catch (error) {
      console.error('❌ [gameAPI] getCharacterEquipmentWithDetails failed:', error);
      throw error;
    }
  },

  async getAllCharactersEquipment(characterIds: string[]): Promise<Record<string, EquippedItemDetails[]>> {
    try {
      console.log('🎒 [gameAPI] Fetching equipment for all characters:', characterIds.length);

      if (characterIds.length === 0) {
        console.log('ℹ️ [gameAPI] No character IDs provided, returning empty map');
        return {};
      }

      const { data, error } = await supabase
        .schema('bubble_game').from('character_equipment')
        .select(`
          *,
          player_item:player_items!inner(
            *,
            item:items!inner(
              *,
              category:item_categories(*)
            )
          )
        `)
        .in('character_id', characterIds);

      if (error) {
        console.error('❌ [gameAPI] Error fetching all equipment:', error);
        throw error;
      }

      console.log('✅ [gameAPI] Total equipment items fetched:', data?.length || 0);

      const equipmentMap: Record<string, EquippedItemDetails[]> = {};

      characterIds.forEach(id => {
        equipmentMap[id] = [];
      });

      (data || []).forEach(eq => {
        const equipment: EquippedItemDetails = {
          ...eq,
          item_name: eq.player_item?.item?.name || '',
          item_display_name: eq.player_item?.item?.display_name || '',
          item_image_url: eq.player_item?.item?.image_url,
          item_rarity: eq.player_item?.item?.rarity || 'common',
          item_stat_bonuses: eq.player_item?.item?.stat_bonuses || {}
        };

        if (!equipmentMap[eq.character_id]) {
          equipmentMap[eq.character_id] = [];
        }
        equipmentMap[eq.character_id].push(equipment);
      });

      console.log('✅ [gameAPI] Equipment organized by character:', Object.keys(equipmentMap).length);
      return equipmentMap;
    } catch (error) {
      console.error('❌ [gameAPI] getAllCharactersEquipment failed:', error);
      throw error;
    }
  },

  // =============================================
  // 商店與技能系統 API
  // =============================================

  async getAllSkills(): Promise<Skill[]> {
    try {
      console.log('🎯 [gameAPI] Fetching all skills...');

      const { data, error } = await supabase
        .schema('bubble_game').from('skills')
        .select('*')
        .order('skill_type')
        .order('skill_name');

      if (error) {
        console.error('❌ [gameAPI] Error fetching skills:', error);
        throw error;
      }

      console.log('✅ [gameAPI] Skills fetched:', data?.length || 0);
      return (data || []) as Skill[];
    } catch (error) {
      console.error('❌ [gameAPI] getAllSkills failed:', error);
      throw error;
    }
  },

  async getAttackModes(): Promise<Skill[]> {
    try {
      console.log('⚔️ [gameAPI] Fetching attack modes...');

      const { data, error } = await supabase
        .schema('bubble_game').from('skills')
        .select('*')
        .eq('skill_type', 'attack_mode')
        .order('skill_name');

      if (error) {
        console.error('❌ [gameAPI] Error fetching attack modes:', error);
        console.error('❌ [gameAPI] Error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        throw error;
      }

      if (!data) {
        console.warn('⚠️ [gameAPI] No attack modes data returned');
        return [];
      }

      console.log('✅ [gameAPI] Attack modes fetched:', data.length);
      return data as Skill[];
    } catch (error: any) {
      console.error('❌ [gameAPI] getAttackModes failed:', error);
      console.error('❌ [gameAPI] Stack trace:', error?.stack);
      return [];
    }
  },

  async getCharacterSkillsFull(characterId: string): Promise<CharacterSkillsFullData> {
    try {
      console.log('🔍 [gameAPI] Fetching character skills full:', characterId);

      if (!characterId) {
        console.error('❌ [gameAPI] No character ID provided');
        return {
          success: false,
          attack_mode_locked: false,
          error: 'No character ID provided'
        };
      }

      const { data, error } = await supabase.rpc('get_character_skills_full', {
        p_character_id: characterId
      });

      if (error) {
        console.error('❌ [gameAPI] Error fetching character skills:', error);
        console.error('❌ [gameAPI] Error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        return {
          success: false,
          attack_mode_locked: false,
          error: error.message
        };
      }

      if (!data) {
        console.warn('⚠️ [gameAPI] No character skills data returned');
        return {
          success: false,
          attack_mode_locked: false,
          error: 'No data returned from server'
        };
      }

      console.log('✅ [gameAPI] Character skills fetched:', data);
      return data as CharacterSkillsFullData;
    } catch (error: any) {
      console.error('❌ [gameAPI] getCharacterSkillsFull failed:', error);
      console.error('❌ [gameAPI] Stack trace:', error?.stack);
      return {
        success: false,
        attack_mode_locked: false,
        error: error?.message || 'Unknown error occurred'
      };
    }
  },

  async selectAttackMode(characterId: string, skillId: string): Promise<SelectAttackModeResult> {
    try {
      console.log('⚡ [gameAPI] Selecting attack mode:', { characterId, skillId });

      if (!characterId || !skillId) {
        console.error('❌ [gameAPI] Missing required parameters');
        return {
          success: false,
          error: 'Missing character ID or skill ID'
        };
      }

      const { data, error } = await supabase.rpc('select_attack_mode', {
        p_character_id: characterId,
        p_skill_id: skillId
      });

      if (error) {
        console.error('❌ [gameAPI] Error selecting attack mode:', error);
        console.error('❌ [gameAPI] Error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        return {
          success: false,
          error: error.message
        };
      }

      if (!data) {
        console.warn('⚠️ [gameAPI] No data returned from select_attack_mode');
        return {
          success: false,
          error: 'No response from server'
        };
      }

      console.log('✅ [gameAPI] Attack mode selected:', data);
      return data as SelectAttackModeResult;
    } catch (error: any) {
      console.error('❌ [gameAPI] selectAttackMode failed:', error);
      console.error('❌ [gameAPI] Stack trace:', error?.stack);
      return {
        success: false,
        error: error?.message || 'Unknown error occurred'
      };
    }
  },

  async checkShopVisitStatus(): Promise<ShopVisitStatus> {
    try {
      console.log('🏪 [gameAPI] Checking shop visit status...');

      const { data, error } = await supabase.rpc('check_shop_visit_status');

      if (error) {
        console.error('❌ [gameAPI] Error checking shop visit status:', error);
        return {
          success: false,
          is_first_visit: true,
          error: error.message
        };
      }

      console.log('✅ [gameAPI] Shop visit status:', data);
      return data as ShopVisitStatus;
    } catch (error: any) {
      console.error('❌ [gameAPI] checkShopVisitStatus failed:', error);
      return {
        success: false,
        is_first_visit: true,
        error: error?.message || 'Unknown error occurred'
      };
    }
  },

  async markShopVisited(): Promise<MarkShopVisitedResult> {
    try {
      console.log('✅ [gameAPI] Marking shop as visited...');

      const { data, error } = await supabase.rpc('mark_shop_visited');

      if (error) {
        console.error('❌ [gameAPI] Error marking shop visited:', error);
        return {
          success: false,
          error: error.message
        };
      }

      console.log('✅ [gameAPI] Shop marked as visited:', data);
      return data as MarkShopVisitedResult;
    } catch (error: any) {
      console.error('❌ [gameAPI] markShopVisited failed:', error);
      return {
        success: false,
        error: error?.message || 'Unknown error occurred'
      };
    }
  },

  // =============================================
  // 商店系統 API
  // =============================================

  async getShopItems(params?: {
    categoryFilter?: string;
    rarityFilter?: string;
    sortBy?: string;
    sortOrder?: string;
    limit?: number;
    offset?: number;
  }): Promise<any> {
    try {
      console.log('🏪 [gameAPI] Fetching shop items...', params);

      const { data, error } = await supabase.rpc('get_shop_items', {
        p_category_filter: params?.categoryFilter || null,
        p_rarity_filter: params?.rarityFilter || null,
        p_sort_by: params?.sortBy || 'shop_display_order',
        p_sort_order: params?.sortOrder || 'ASC',
        p_limit: params?.limit || 100,
        p_offset: params?.offset || 0
      });

      if (error) {
        console.error('❌ [gameAPI] Error fetching shop items:', error);
        return {
          success: false,
          items: [],
          error: error.message
        };
      }

      console.log('✅ [gameAPI] Shop items fetched:', data);
      return data;
    } catch (error: any) {
      console.error('❌ [gameAPI] getShopItems failed:', error);
      return {
        success: false,
        items: [],
        error: error?.message || 'Unknown error occurred'
      };
    }
  },

  async purchaseShopItem(
    itemId: string,
    quantity: number = 1,
    currencyType: 'BBG' | 'BBT' | 'WLD' = 'BBG',
    burnTxHash?: string,
    burnAmount?: number
  ): Promise<any> {
    try {
      console.log('💳 [gameAPI] Purchasing shop item...', {
        itemId,
        quantity,
        currencyType,
        burnTxHash,
        burnAmount
      });

      const { data, error } = await supabase.rpc('purchase_shop_item', {
        p_item_id: itemId,
        p_quantity: quantity,
        p_currency_type: currencyType,
        p_burn_tx_hash: burnTxHash || null,
        p_burn_amount: burnAmount || 0
      });

      if (error) {
        console.error('❌ [gameAPI] Error purchasing item:', error);

        // In-Game Wallet 錯誤處理
        let errorMessage = error.message;
        if (error.message.includes('IN_GAME_WALLET_INSUFFICIENT')) {
          errorMessage = 'In-Game Wallet BBG 餘額不足';
        } else if (error.message.includes('IN_GAME_WALLET_NOT_FOUND')) {
          errorMessage = 'In-Game Wallet 不存在，請先儲值';
        } else if (error.message.includes('IN_GAME_WALLET_ERROR')) {
          errorMessage = 'In-Game Wallet 扣款失敗';
        }

        return {
          success: false,
          error: errorMessage
        };
      }

      const normalizedData = normalizeInGameWalletLedgerResult(data, {
        preserveOnChainHash: currencyType !== 'BBG',
      });

      if (!normalizedData || !normalizedData.success) {
        const errorMsg = normalizedData?.error || normalizedData?.message || 'Failed to purchase item';
        console.error('❌ [gameAPI] Purchase item failed:', errorMsg);

        // In-Game Wallet 錯誤處理
        let errorMessage = errorMsg;
        if (errorMsg.includes('IN_GAME_WALLET_INSUFFICIENT')) {
          errorMessage = 'In-Game Wallet BBG 餘額不足';
        } else if (errorMsg.includes('IN_GAME_WALLET_NOT_FOUND')) {
          errorMessage = 'In-Game Wallet 不存在，請先儲值';
        } else if (errorMsg.includes('IN_GAME_WALLET_ERROR')) {
          errorMessage = 'In-Game Wallet 扣款失敗';
        }

        return {
          success: false,
          error: errorMessage,
          ...(normalizedData || {})
        };
      }

      console.log('✅ [gameAPI] Item purchased successfully:', normalizedData);
      return normalizedData;
    } catch (error: any) {
      console.error('❌ [gameAPI] purchaseShopItem failed:', error);

      // In-Game Wallet 錯誤處理
      let errorMessage = error?.message || 'Unknown error occurred';
      if (errorMessage.includes('IN_GAME_WALLET_INSUFFICIENT')) {
        errorMessage = 'In-Game Wallet BBG 餘額不足';
      } else if (errorMessage.includes('IN_GAME_WALLET_NOT_FOUND')) {
        errorMessage = 'In-Game Wallet 不存在，請先儲值';
      } else if (errorMessage.includes('IN_GAME_WALLET_ERROR')) {
        errorMessage = 'In-Game Wallet 扣款失敗';
      }

      return {
        success: false,
        error: errorMessage
      };
    }
  },

  async verifyAndPurchaseWithToken(
    worldTransactionId: string,
    tokenSymbol: 'BBT' | 'WLD',
    purchaseType: 'character_asset' | 'shop_item',
    itemId: string,
    quantity: number = 1,
    options?: { receipt_id?: string }
  ): Promise<any> {
    try {
      const receiptId = options?.receipt_id ?? worldTransactionId;
      const isReceiptMode = !!options?.receipt_id;

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('未授權，請重新登入');
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.user_metadata?.wallet_address) {
        throw new Error('請先綁定錢包地址');
      }

      const userWalletAddress = user.user_metadata.wallet_address;

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const functionUrl = `${supabaseUrl}/functions/v1/verify-purchase-transaction`;

      const body = isReceiptMode
        ? { receipt_id: receiptId, purchase_type: purchaseType, item_id: itemId, quantity }
        : {
            world_transaction_id: worldTransactionId,
            expected_token_symbol: tokenSymbol,
            user_wallet_address: userWalletAddress,
            purchase_type: purchaseType,
            item_id: itemId,
            quantity,
          };

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [gameAPI] Edge Function 請求失敗:', response.status, errorText);
        throw new Error(`驗證失敗 (${response.status}): ${errorText}`);
      }

      const result = await response.json();

      if (!result.success) {
        console.error('❌ [gameAPI] 購買驗證失敗:', result);

        // 友善錯誤訊息
        let errorMessage = result.error || result.message || '購買驗證失敗';

        switch (result.error) {
          case 'WORLDCOIN_POLL_TIMEOUT':
            errorMessage = '無法獲取交易資訊，請稍後再試';
            break;
          case 'CHAIN_VERIFICATION_FAILED':
            errorMessage = '鏈上驗證失敗，交易可能尚未確認';
            break;
          case 'AMOUNT_MISMATCH':
            errorMessage = '交易金額不符，請重新購買';
            break;
          case 'INVALID_RECIPIENT':
            errorMessage = '收款地址錯誤';
            break;
          case 'TRANSACTION_FAILED':
            errorMessage = '交易失敗或已回退';
            break;
        }

        return {
          success: false,
          error: errorMessage,
          details: result
        };
      }

      console.log('✅ [gameAPI] 購買驗證成功:', result);
      return {
        success: true,
        ...result
      };
    } catch (error: any) {
      console.error('❌ [gameAPI] verifyAndPurchaseWithToken failed:', error);
      return {
        success: false,
        error: error?.message || '購買驗證過程發生錯誤'
      };
    }
  },

  async getUserShopPurchases(params?: {
    limit?: number;
    offset?: number;
  }): Promise<any> {
    try {
      console.log('📜 [gameAPI] Fetching user shop purchases...', params);

      const { data, error } = await supabase.rpc('get_user_shop_purchases', {
        p_limit: params?.limit || 50,
        p_offset: params?.offset || 0
      });

      if (error) {
        console.error('❌ [gameAPI] Error fetching purchases:', error);
        return {
          success: false,
          purchases: [],
          error: error.message
        };
      }

      console.log('✅ [gameAPI] Purchases fetched:', data);
      return data;
    } catch (error: any) {
      console.error('❌ [gameAPI] getUserShopPurchases failed:', error);
      return {
        success: false,
        purchases: [],
        error: error?.message || 'Unknown error occurred'
      };
    }
  },

  // =============================================
  // 挖礦系統 API
  // =============================================

  async attemptMining(characterId: string): Promise<MiningResult> {
    try {
      console.log('⛏️ [gameAPI] Attempting mining with character:', characterId);

      const { data, error } = await supabase.rpc('attempt_mining', {
        p_character_id: characterId
      });

      if (error) {
        console.error('❌ [gameAPI] Error attempting mining:', error);
        return {
          success: false,
          remaining_energy: 0,
          error: error.message
        };
      }

      console.log('✅ [gameAPI] Mining attempt result:', data);
      return data;
    } catch (error: any) {
      console.error('❌ [gameAPI] attemptMining failed:', error);
      return {
        success: false,
        remaining_energy: 0,
        error: error?.message || 'Unknown error occurred'
      };
    }
  },

  async getAvailableMiningResources(raceId: string): Promise<GetAvailableMiningResourcesResult[]> {
    try {
      console.log('📋 [gameAPI] Fetching available mining resources for race:', raceId);

      const { data, error } = await supabase.rpc('get_available_mining_resources', {
        p_race_id: raceId
      });

      if (error) {
        console.error('❌ [gameAPI] Error fetching mining resources:', error);
        return [];
      }

      console.log('✅ [gameAPI] Mining resources fetched:', data?.length || 0, 'resources');
      return data || [];
    } catch (error: any) {
      console.error('❌ [gameAPI] getAvailableMiningResources failed:', error);
      return [];
    }
  },

  async recoverUserEnergy(): Promise<{
    success: boolean;
    recovered: boolean;
    current_energy?: number;
    max_energy?: number;
    recovery_amount?: number;
    error?: string;
  }> {
    try {
      console.log('🔄 [gameAPI] Recovering user energy...');

      const { data, error } = await supabase.rpc('recover_user_energy');

      if (error) {
        console.error('❌ [gameAPI] Error recovering energy:', error);
        return {
          success: false,
          recovered: false,
          error: error.message
        };
      }

      if (data?.recovered) {
        console.log('✅ [gameAPI] Energy recovered:', data);
      } else {
        console.log('ℹ️ [gameAPI] Energy recovery not needed yet:', data?.message);
      }

      return data as any;
    } catch (error: any) {
      console.error('❌ [gameAPI] recoverUserEnergy failed:', error);
      return {
        success: false,
        recovered: false,
        error: error?.message || 'Unknown error occurred'
      };
    }
  },

  async getEnergyInfo(): Promise<EnergyInfo | null> {
    try {
      console.log('⚡ [gameAPI] Fetching energy info...');

      const { data: user } = await supabase.auth.getUser();
      if (!user?.user?.id) {
        console.error('❌ [gameAPI] User not authenticated');
        return null;
      }

      // 先執行體力回復檢查
      await this.recoverUserEnergy();

      const { data, error } = await supabase.rpc('get_player_energy_info', {
        p_user_id: user.user.id,
      });

      if (error) {
        console.error('❌ [gameAPI] Error fetching energy info:', error);
        return null;
      }

      const energyResult = data as {
        success?: boolean;
        current_energy?: number;
        max_energy?: number;
        energy_recovery_rate?: number;
        last_energy_recovery?: string;
      } | null;

      if (
        !energyResult ||
        energyResult.success !== true ||
        typeof energyResult.current_energy !== 'number' ||
        typeof energyResult.max_energy !== 'number' ||
        typeof energyResult.energy_recovery_rate !== 'number' ||
        typeof energyResult.last_energy_recovery !== 'string'
      ) {
        return null;
      }

      const lastRecovery = new Date(energyResult.last_energy_recovery);
      const now = new Date();
      const minutesPassed = Math.floor((now.getTime() - lastRecovery.getTime()) / 60000);
      const nextRecoveryIn = 60 - (minutesPassed % 60);

      const energyInfo: EnergyInfo = {
        current: energyResult.current_energy,
        max: energyResult.max_energy,
        recovery_rate: energyResult.energy_recovery_rate,
        last_recovery: energyResult.last_energy_recovery,
        next_recovery_in_minutes: nextRecoveryIn
      };

      console.log('✅ [gameAPI] Energy info fetched:', energyInfo);
      return energyInfo;
    } catch (error: any) {
      console.error('❌ [gameAPI] getEnergyInfo failed:', error);
      return null;
    }
  },

  async getMiningStatistics(): Promise<MiningStatistics | null> {
    try {
      console.log('📊 [gameAPI] Fetching mining statistics...');

      const { data: user } = await supabase.auth.getUser();
      if (!user?.user?.id) {
        console.error('❌ [gameAPI] User not authenticated');
        return null;
      }

      const { data: attempts, error } = await supabase
        .schema('bubble_game').from('mining_attempts')
        .select('success, item_id, items(name, rarity)')
        .eq('user_id', user.user.id);

      if (error) {
        console.error('❌ [gameAPI] Error fetching mining statistics:', error);
        return null;
      }

      const totalAttempts = attempts?.length || 0;
      const successCount = attempts?.filter((a: any) => a.success).length || 0;
      const successRate = totalAttempts > 0 ? successCount / totalAttempts : 0;

      const resourcesByType: Record<string, number> = {};
      let rareCount = 0;
      let epicCount = 0;
      let legendaryCount = 0;

      attempts?.forEach((attempt: any) => {
        if (attempt.success && attempt.items) {
          const itemName = attempt.items.name;
          resourcesByType[itemName] = (resourcesByType[itemName] || 0) + 1;

          if (attempt.items.rarity === 'rare') rareCount++;
          if (attempt.items.rarity === 'epic') epicCount++;
          if (attempt.items.rarity === 'legendary') legendaryCount++;
        }
      });

      const statistics: MiningStatistics = {
        total_attempts: totalAttempts,
        success_count: successCount,
        success_rate: successRate,
        total_resources_obtained: successCount,
        rare_resources_count: rareCount,
        epic_resources_count: epicCount,
        legendary_resources_count: legendaryCount,
        resources_by_type: resourcesByType
      };

      console.log('✅ [gameAPI] Mining statistics fetched:', statistics);
      return statistics;
    } catch (error: any) {
      console.error('❌ [gameAPI] getMiningStatistics failed:', error);
      return null;
    }
  },

  async getMiningResources(): Promise<MiningResource[]> {
    try {
      console.log('💎 [gameAPI] Fetching all mining resources...');

      const { data, error } = await supabase
        .schema('bubble_game').from('items')
        .select('*, category:item_categories(*)')
        .eq('source_type', 'mining')
        .order('base_drop_rate', { ascending: false });

      if (error) {
        console.error('❌ [gameAPI] Error fetching mining resources:', error);
        return [];
      }

      console.log('✅ [gameAPI] Mining resources fetched:', data?.length || 0, 'resources');
      return data as MiningResource[] || [];
    } catch (error: any) {
      console.error('❌ [gameAPI] getMiningResources failed:', error);
      return [];
    }
  },

  // =============================================
  // 放置型網格挖礦地圖系統 API
  // =============================================

  async getMiningMaps(): Promise<MiningMap[]> {
    try {
      console.log('🗺️ [gameAPI] Fetching mining maps...');

      const { data, error } = await supabase
        .schema('bubble_game').from('mining_maps')
        .select('*')
        .eq('is_active', true)
        .order('created_at');

      if (error) {
        console.error('❌ [gameAPI] Error fetching mining maps:', error);
        return [];
      }

      console.log('✅ [gameAPI] Mining maps fetched:', data?.length || 0, 'maps');
      return (data || []) as MiningMap[];
    } catch (error: any) {
      console.error('❌ [gameAPI] getMiningMaps failed:', error);
      return [];
    }
  },

  async getMiningNodes(mapId: string): Promise<MiningNode[]> {
    try {
      console.log('📍 [gameAPI] Fetching mining nodes for map:', mapId);

      const { data, error } = await supabase
        .schema('bubble_game').from('mining_nodes_with_live_count')
        .select('*')
        .eq('map_id', mapId)
        .order('grid_x')
        .order('grid_y');

      if (error) {
        console.error('❌ [gameAPI] Error fetching mining nodes:', error);
        return [];
      }

      const normalizedNodes = (data || []).map((node: any) => ({
        ...node,
        active_miners_count: node.active_miners_count_live ?? node.active_miners_count ?? 0
      }));

      console.log('✅ [gameAPI] Mining nodes fetched:', normalizedNodes.length, 'nodes');
      return normalizedNodes as MiningNode[];
    } catch (error: any) {
      console.error('❌ [gameAPI] getMiningNodes failed:', error);
      return [];
    }
  },

  async getMiningNodesInRange(
    mapId: string,
    minX: number,
    maxX: number,
    minY: number,
    maxY: number
  ): Promise<MiningNode[]> {
    try {
      console.log('📍 [gameAPI] Fetching mining nodes in range:', {
        mapId,
        x: `${minX}-${maxX}`,
        y: `${minY}-${maxY}`
      });

      const { data, error } = await supabase
        .schema('bubble_game').from('mining_nodes_with_live_count')
        .select('*')
        .eq('map_id', mapId)
        .gte('grid_x', minX)
        .lte('grid_x', maxX)
        .gte('grid_y', minY)
        .lte('grid_y', maxY)
        .order('grid_x')
        .order('grid_y');

      if (error) {
        console.error('❌ [gameAPI] Error fetching mining nodes in range:', error);
        return [];
      }

      const normalizedNodes = (data || []).map((node: any) => ({
        ...node,
        active_miners_count: node.active_miners_count_live ?? node.active_miners_count ?? 0
      }));

      console.log('✅ [gameAPI] Mining nodes in range fetched:', normalizedNodes.length, 'nodes');
      return normalizedNodes as MiningNode[];
    } catch (error: any) {
      console.error('❌ [gameAPI] getMiningNodesInRange failed:', error);
      return [];
    }
  },

  async getNodeDetails(nodeId: string): Promise<NodeDetails | null> {
    try {
      console.log('🔍 [gameAPI] Fetching node details:', nodeId);

      const { data, error } = await supabase.rpc('get_node_details', {
        p_node_id: nodeId
      });

      if (error) {
        console.error('❌ [gameAPI] Error fetching node details:', error);
        return null;
      }

      console.log('✅ [gameAPI] Node details fetched:', data);
      return data as NodeDetails;
    } catch (error: any) {
      console.error('❌ [gameAPI] getNodeDetails failed:', error);
      return null;
    }
  },

  async getNodeMiners(nodeId: string): Promise<NodeMinerInfo[]> {
    try {
      console.log('👥 [gameAPI] Fetching node miners:', nodeId);

      const { data, error } = await supabase.rpc('get_node_miners', {
        p_node_id: nodeId
      });

      if (error) {
        console.error('❌ [gameAPI] Error fetching node miners:', error);
        return [];
      }

      console.log('✅ [gameAPI] Node miners fetched:', data);
      return data as NodeMinerInfo[];
    } catch (error: any) {
      console.error('❌ [gameAPI] getNodeMiners failed:', error);
      return [];
    }
  },

  async getMapActiveMinersCount(mapId: string): Promise<number> {
    try {
      console.log('📊 [gameAPI] Fetching map active miners count:', mapId);

      const { data, error } = await supabase.rpc('get_map_active_miners_count', {
        p_map_id: mapId
      });

      if (error) {
        console.error('❌ [gameAPI] Error fetching map active miners count:', error);
        return 0;
      }

      console.log('✅ [gameAPI] Map active miners count:', data);
      return data || 0;
    } catch (error: any) {
      console.error('❌ [gameAPI] getMapActiveMinersCount failed:', error);
      return 0;
    }
  },

  async getMapAllActiveMiners(mapId: string): Promise<MiningSessionWithDetails[]> {
    try {
      console.log('👥 [gameAPI] Fetching all active miners on map:', mapId);

      const { data, error } = await supabase.rpc('get_map_all_active_miners', {
        p_map_id: mapId
      });

      if (error) {
        console.error('❌ [gameAPI] Error fetching map all active miners:', error);
        return [];
      }

      console.log(`✅ [gameAPI] Fetched ${data?.length || 0} active miners on map`);
      return (data || []) as MiningSessionWithDetails[];
    } catch (error: any) {
      console.error('❌ [gameAPI] getMapAllActiveMiners failed:', error);
      return [];
    }
  },

  async calculateCharacterMiningStats(characterId: string): Promise<CharacterMiningStats | null> {
    try {
      console.log('📊 [gameAPI] Calculating mining stats for character:', characterId);

      const { data: character, error } = await supabase
        .schema('bubble_game').from('player_characters')
        .select('*, color:character_colors(*), race:character_races(*)')
        .eq('id', characterId)
        .maybeSingle();

      if (error || !character) {
        console.error('❌ [gameAPI] Error fetching character:', error);
        return null;
      }

      const stats = character.final_stats;
      const power = stats.power || 0;
      const defense = stats.defense || 0;
      const agility = stats.agility || 0;
      const luck = stats.luck || 0;
      const charisma = stats.charisma || 0;
      const bubble = character.current_bubble || 0;
      const maxBubble = character.max_bubble || 100;

      const bubbleMultiplier = Math.max(0, bubble / maxBubble);
      const effectivePower = power * bubbleMultiplier;
      const effectiveAgility = agility * bubbleMultiplier;
      const effectiveDefense = defense * bubbleMultiplier;

      const miningSpeed = Math.max(10, Math.min(60, Math.floor(60 / (1 + (effectivePower + effectiveAgility) / 200))));
      const backpackCapacity = 10 + Math.floor(effectiveDefense / 10);

      const canMine = bubble >= 50 && character.is_active;
      const reason = !canMine
        ? bubble < 50
          ? '泡泡不足(需要 50 以上)'
          : '角色未啟用'
        : undefined;

      const result: CharacterMiningStats = {
        character_id: characterId,
        power,
        defense,
        agility,
        luck,
        charisma,
        sweetness: bubble,
        mining_speed_seconds: miningSpeed,
        backpack_capacity: backpackCapacity,
        can_mine: canMine,
        reason
      };

      console.log('✅ [gameAPI] Mining stats calculated:', result);
      return result;
    } catch (error: any) {
      console.error('❌ [gameAPI] calculateCharacterMiningStats failed:', error);
      return null;
    }
  },

  async startMiningSession(characterId: string, nodeId: string): Promise<StartMiningSessionResult> {
    try {
      console.log('⛏️ [gameAPI] Starting mining session:', { characterId, nodeId });

      const { data, error } = await supabase.rpc('start_mining_session', {
        p_character_id: characterId,
        p_node_id: nodeId
      });

      if (error) {
        console.error('❌ [gameAPI] Error starting mining session:', error);
        return {
          success: false,
          error: error.message
        };
      }

      console.log('✅ [gameAPI] Mining session started:', data);
      return data as StartMiningSessionResult;
    } catch (error: any) {
      console.error('❌ [gameAPI] startMiningSession failed:', error);
      return {
        success: false,
        error: error?.message || 'Unknown error occurred'
      };
    }
  },

  async processMiningTick(sessionId: string): Promise<ProcessMiningTickResult> {
    try {
      console.log('⚙️ [gameAPI] Processing mining tick:', sessionId);

      const { data, error } = await supabase.rpc('process_mining_tick', {
        p_session_id: sessionId
      });

      if (error) {
        console.error('❌ [gameAPI] Error processing mining tick:', error);
        return {
          success: false,
          error: error.message
        };
      }

      console.log('✅ [gameAPI] Mining tick processed:', data);
      return data as ProcessMiningTickResult;
    } catch (error: any) {
      console.error('❌ [gameAPI] processMiningTick failed:', error);
      return {
        success: false,
        error: error?.message || 'Unknown error occurred'
      };
    }
  },

  async harvestMiningSession(sessionId: string): Promise<HarvestResult> {
    try {
      console.log('🎁 [gameAPI] Harvesting mining session:', sessionId);

      const { data, error } = await supabase.rpc('harvest_mining_session', {
        p_session_id: sessionId
      });

      if (error) {
        console.error('❌ [gameAPI] Error harvesting mining session:', error);
        return {
          success: false,
          error: error.message
        };
      }

      const harvestResult = data as HarvestResult;

      if (harvestResult?.success && harvestResult.items_harvested?.length) {
        const missingNameIds = harvestResult.items_harvested
          .filter(item => !item.item_name)
          .map(item => item.item_id)
          .filter(Boolean);

        if (missingNameIds.length > 0) {
          const { data: items, error: itemsError } = await supabase
            .schema('bubble_game').from('items')
            .select('id, name')
            .in('id', missingNameIds);

          if (itemsError) {
            console.error('❌ [gameAPI] Error fetching harvested item names:', itemsError);
          } else {
            const itemNameMap = (items || []).reduce<Record<string, string>>((acc, item) => {
              acc[item.id] = item.name;
              return acc;
            }, {});

            harvestResult.items_harvested = harvestResult.items_harvested.map(item => ({
              ...item,
              item_name: item.item_name || itemNameMap[item.item_id]
            }));
          }
        }
      }

      console.log('✅ [gameAPI] Mining session harvested:', harvestResult);
      return harvestResult;
    } catch (error: any) {
      console.error('❌ [gameAPI] harvestMiningSession failed:', error);
      return {
        success: false,
        error: error?.message || 'Unknown error occurred'
      };
    }
  },

  async cancelMiningSession(sessionId: string): Promise<CancelMiningSessionResult> {
    try {
      console.log('❌ [gameAPI] Cancelling mining session:', sessionId);

      const { data, error } = await supabase.rpc('cancel_mining_session', {
        p_session_id: sessionId
      });

      if (error) {
        console.error('❌ [gameAPI] Error cancelling mining session:', error);
        return {
          success: false,
          error: error.message
        };
      }

      console.log('✅ [gameAPI] Mining session cancelled:', data);
      return data as CancelMiningSessionResult;
    } catch (error: any) {
      console.error('❌ [gameAPI] cancelMiningSession failed:', error);
      return {
        success: false,
        error: error?.message || 'Unknown error occurred'
      };
    }
  },

  async getUserMiningSessions(): Promise<MiningSessionWithDetails[]> {
    try {
      console.log('📋 [gameAPI] Fetching user mining sessions...');

      const { data, error } = await supabase.rpc('get_user_mining_sessions');

      if (error) {
        console.error('❌ [gameAPI] Error fetching user mining sessions:', error);
        return [];
      }

      console.log('✅ [gameAPI] User mining sessions fetched:', data?.length || 0, 'sessions');
      return (data || []) as MiningSessionWithDetails[];
    } catch (error: any) {
      console.error('❌ [gameAPI] getUserMiningSessions failed:', error);
      return [];
    }
  },

  async getMapActiveSessions(mapId: string): Promise<ActiveMinerInfo[]> {
    try {
      console.log('👥 [gameAPI] Fetching map active sessions:', mapId);

      const { data, error } = await supabase.rpc('get_map_active_sessions', {
        p_map_id: mapId
      });

      if (error) {
        console.error('❌ [gameAPI] Error fetching map active sessions:', error);
        return [];
      }

      console.log('✅ [gameAPI] Map active sessions fetched:', data?.length || 0, 'sessions');
      return (data || []) as ActiveMinerInfo[];
    } catch (error: any) {
      console.error('❌ [gameAPI] getMapActiveSessions failed:', error);
      return [];
    }
  },

  async getNearbyCharismaBonus(nodeId: string, characterId: string): Promise<CharismaBonus | null> {
    try {
      console.log('✨ [gameAPI] Calculating nearby charisma bonus:', { nodeId, characterId });

      const { data: bonusValue, error } = await supabase.rpc('get_nearby_charisma_bonus', {
        p_node_id: nodeId,
        p_character_id: characterId
      });

      if (error) {
        console.error('❌ [gameAPI] Error calculating charisma bonus:', error);
        return null;
      }

      const { data: nearbyPlayers } = await supabase
        .schema('bubble_game').from('active_mining_sessions')
        .select(`
          character_id,
          character_charisma,
          node_id,
          mining_nodes!inner(grid_x, grid_y, map_id),
          player_characters!inner(color:character_colors(display_name), race:character_races(display_name))
        `)
        .eq('status', 'active')
        .neq('character_id', characterId);

      const totalCharisma = Math.floor((bonusValue || 0) * 500);

      const result: CharismaBonus = {
        total_charisma: totalCharisma,
        bonus_percentage: bonusValue || 0,
        nearby_players: (nearbyPlayers || []).map((p: any) => ({
          character_id: p.character_id,
          character_name: `${p.player_characters?.color?.display_name || ''} ${p.player_characters?.race?.display_name || ''}`,
          charisma: p.character_charisma,
          grid_x: p.mining_nodes?.grid_x || 0,
          grid_y: p.mining_nodes?.grid_y || 0
        }))
      };

      console.log('✅ [gameAPI] Charisma bonus calculated:', result);
      return result;
    } catch (error: any) {
      console.error('❌ [gameAPI] getNearbyCharismaBonus failed:', error);
      return null;
    }
  },

  // =============================================
  // 角色合成系統 API
  // =============================================

  async getSynthesisCost(
    currentLevel: number,
    isDevil: boolean = false,
    raceName?: string
  ): Promise<SynthesisCostBreakdown | null> {
    try {
      console.log('💰 [gameAPI] Getting synthesis cost:', { currentLevel, isDevil, raceName });

      const { data, error } = await supabase.rpc('calculate_synthesis_cost', {
        p_current_level: currentLevel,
        p_is_devil: isDevil,
        p_race_name: raceName
      });

      if (error) {
        console.error('❌ [gameAPI] Error getting synthesis cost:', error);
        return {
          current_level: currentLevel,
          next_level: currentLevel + 1,
          bbg_cost: 0,
          material_character_count: 0,
          required_materials: [],
          new_stat_cap: 0
        };
      }

      if (!data) {
        console.warn('⚠️ [gameAPI] No synthesis cost data returned');
        return {
          current_level: currentLevel,
          next_level: currentLevel + 1,
          bbg_cost: 0,
          material_character_count: 0,
          required_materials: [],
          new_stat_cap: 0
        };
      }

      console.log('✅ [gameAPI] Synthesis cost retrieved:', data);

      // 確保 required_materials 是陣列
      const safeCost: SynthesisCostBreakdown = {
        current_level: data.current_level || currentLevel,
        next_level: data.next_level || (currentLevel + 1),
        bbg_cost: typeof data.bbg_cost === 'number' ? data.bbg_cost : 0,
        material_character_count: typeof data.material_character_count === 'number' ? data.material_character_count : 0,
        required_materials: Array.isArray(data.required_materials) ? data.required_materials : [],
        current_stat_cap: typeof data.current_stat_cap === 'number' ? data.current_stat_cap : undefined,
        new_stat_cap: typeof data.new_stat_cap === 'number' ? data.new_stat_cap : 0,
        stat_cap_increase: typeof data.stat_cap_increase === 'number' ? data.stat_cap_increase : undefined
      };

      return safeCost;
    } catch (error: any) {
      console.error('❌ [gameAPI] getSynthesisCost failed:', error);
      return {
        current_level: currentLevel,
        next_level: currentLevel + 1,
        bbg_cost: 0,
        material_character_count: 0,
        required_materials: [],
        new_stat_cap: 0
      };
    }
  },

  async getSynthesizableCharacters(): Promise<GetSynthesizablePairsResult | null> {
    try {
      console.log('🔄 [gameAPI] Getting synthesizable character pairs...');
      console.log('🔄 [gameAPI] Calling RPC with empty params object to avoid default value issue');

      const { data, error } = await supabase.rpc('get_synthesizable_pairs', {});

      if (error) {
        console.error('❌ [gameAPI] Error getting synthesizable pairs:', error);
        console.error('❌ [gameAPI] Full error object:', JSON.stringify(error, null, 2));
        console.error('❌ [gameAPI] Error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code,
          stack: error.stack || 'No stack trace'
        });
        return {
          synthesizable_groups: [],
          total_groups: 0
        };
      }

      console.log('✅ [gameAPI] Raw data from database:', data);
      console.log('✅ [gameAPI] Data type:', typeof data);
      console.log('✅ [gameAPI] Is array?:', Array.isArray(data));
      console.log('✅ [gameAPI] Data keys:', data ? Object.keys(data) : 'null');
      console.log('✅ [gameAPI] Data structure:', JSON.stringify(data, null, 2));

      // 防禦性檢查:確保返回正確的資料結構
      if (!data) {
        console.warn('⚠️ [gameAPI] Database returned null/undefined data, returning empty result');
        return {
          synthesizable_groups: [],
          total_groups: 0
        };
      }

      // 檢查是否為預期的物件結構
      if (typeof data !== 'object') {
        console.error('❌ [gameAPI] Unexpected data type, expected object, got:', typeof data);
        return {
          synthesizable_groups: [],
          total_groups: 0
        };
      }

      // 檢查 synthesizable_groups 是否存在且為陣列
      if (!data.synthesizable_groups) {
        console.warn('⚠️ [gameAPI] synthesizable_groups is missing in response:', data);
        return {
          synthesizable_groups: [],
          total_groups: 0
        };
      }

      if (!Array.isArray(data.synthesizable_groups)) {
        console.error('❌ [gameAPI] synthesizable_groups is not an array:', {
          type: typeof data.synthesizable_groups,
          value: data.synthesizable_groups
        });
        return {
          synthesizable_groups: [],
          total_groups: 0
        };
      }

      // 驗證每個 group 的 characters 欄位是否為陣列
      const validatedGroups = (data.synthesizable_groups || []).map((group: any) => {
        // 確保所有必要屬性存在
        const safeGroup = {
          ...group,
          color: group.color || { name: 'unknown', display_name: '未知顏色', hex_code: '#808080' },
          race: group.race || { name: 'unknown', display_name: '未知種族' },
          current_level: group.current_level || 1,
          next_level: group.next_level || 2,
          owned_count: group.owned_count || 0,
          required_count: group.required_count || 0,
          can_synthesize: group.can_synthesize || false,
          max_synthesis_times: group.max_synthesis_times || 0,
          characters: Array.isArray(group.characters) ? group.characters : []
        };

        if (!group.characters || !Array.isArray(group.characters)) {
          console.warn('⚠️ [gameAPI] Group has invalid characters field:', group);
        }

        return safeGroup;
      });

      console.log(`✅ [gameAPI] Successfully retrieved ${validatedGroups.length} synthesizable groups`);

      // 確保返回的結果符合型別定義
      const safeResult: GetSynthesizablePairsResult = {
        synthesizable_groups: Array.isArray(validatedGroups) ? validatedGroups : [],
        total_groups: typeof data.total_groups === 'number' ? data.total_groups : validatedGroups.length
      };

      return safeResult;
    } catch (error: any) {
      console.error('❌ [gameAPI] getSynthesizableCharacters failed with exception:', error);
      console.error('❌ [gameAPI] Error stack:', error.stack);
      return {
        synthesizable_groups: [],
        total_groups: 0
      };
    }
  },

  async validateSynthesisEligibility(
    mainCharacterId: string,
    materialCharacterIds: string[]
  ): Promise<SynthesisValidationResult> {
    try {
      console.log('✔️ [gameAPI] Validating synthesis eligibility:', {
        mainCharacterId,
        materialCount: materialCharacterIds.length
      });

      const { data, error } = await supabase.rpc('validate_synthesis_eligibility', {
        p_main_character_id: mainCharacterId,
        p_material_character_ids: materialCharacterIds
      });

      if (error) {
        console.error('❌ [gameAPI] Error validating synthesis:', error);
        return {
          valid: false,
          error: error.message
        };
      }

      console.log('✅ [gameAPI] Synthesis validation result:', data);
      return data as SynthesisValidationResult;
    } catch (error: any) {
      console.error('❌ [gameAPI] validateSynthesisEligibility failed:', error);
      return {
        valid: false,
        error: error?.message || 'Unknown error occurred'
      };
    }
  },

  async getMaterialCharactersSkins(
    materialCharacterIds: string[],
    mainCharacterId?: string
  ): Promise<GetMaterialCharactersSkinsResult> {
    try {
      console.log('🎨 [gameAPI] Getting material characters skins:', {
        characterCount: materialCharacterIds.length,
        mainCharacterId
      });

      const { data, error } = await supabase.rpc('get_material_characters_skins', {
        p_character_ids: materialCharacterIds,
        p_main_character_id: mainCharacterId || null
      });

      if (error) {
        console.error('❌ [gameAPI] Error getting material characters skins:', error);
        return {
          success: false,
          skins: [],
          total_count: 0,
          non_default_count: 0,
          already_owned_count: 0,
          will_transfer_count: 0,
          error: error.message
        };
      }

      console.log('✅ [gameAPI] Material characters skins retrieved:', {
        totalCount: data.total_count,
        nonDefaultCount: data.non_default_count,
        willTransferCount: data.will_transfer_count
      });

      return data as GetMaterialCharactersSkinsResult;
    } catch (error: any) {
      console.error('❌ [gameAPI] getMaterialCharactersSkins failed:', error);
      return {
        success: false,
        skins: [],
        total_count: 0,
        non_default_count: 0,
        already_owned_count: 0,
        will_transfer_count: 0,
        error: error?.message || 'Unknown error occurred'
      };
    }
  },

  async synthesizeCharacter(
    mainCharacterId: string,
    materialCharacterIds: string[]
  ): Promise<CharacterSynthesisResult> {
    try {
      console.log('⚗️ [gameAPI] Synthesizing character (In-Game Wallet):', {
        mainCharacterId,
        materialCount: materialCharacterIds.length
      });

      const { data, error } = await supabase.rpc('synthesize_characters', {
        p_main_character_id: mainCharacterId,
        p_material_character_ids: materialCharacterIds
      });

      if (error) {
        console.error('❌ [gameAPI] Error synthesizing character:', error);

        // 處理 In-Game Wallet 相關錯誤
        let errorMessage = error.message;
        if (error.message.includes('IN_GAME_WALLET_INSUFFICIENT')) {
          errorMessage = 'In-Game Wallet BBG 餘額不足';
        } else if (error.message.includes('IN_GAME_WALLET_NOT_FOUND')) {
          errorMessage = 'In-Game Wallet 不存在，請先儲值';
        } else if (error.message.includes('IN_GAME_WALLET_ERROR')) {
          errorMessage = 'In-Game Wallet 扣款失敗';
        }

        return {
          success: false,
          error: errorMessage
        };
      }

      const normalizedData = normalizeInGameWalletLedgerResult(data);
      console.log('✅ [gameAPI] Character synthesis completed (In-Game Wallet):', normalizedData);
      return normalizedData as CharacterSynthesisResult & InGameWalletLedgerResult;
    } catch (error: any) {
      console.error('❌ [gameAPI] synthesizeCharacter failed:', error);
      return {
        success: false,
        error: error?.message || 'Unknown error occurred'
      };
    }
  },

  async getSynthesisHistory(limit: number = 20): Promise<GetSynthesisHistoryResult> {
    try {
      console.log('📜 [gameAPI] Getting synthesis history...');

      const { data, error } = await supabase
        .schema('bubble_game').from('character_synthesis_history')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('❌ [gameAPI] Error getting synthesis history:', error);
        return {
          success: false,
          history: [],
          total_count: 0,
          error: error.message
        };
      }

      console.log('✅ [gameAPI] Synthesis history retrieved:', data?.length || 0, 'records');
      return {
        success: true,
        history: data || [],
        total_count: data?.length || 0
      };
    } catch (error: any) {
      console.error('❌ [gameAPI] getSynthesisHistory failed:', error);
      return {
        success: false,
        history: [],
        total_count: 0,
        error: error?.message || 'Unknown error occurred'
      };
    }
  },

  async getSynthesisMilestones(): Promise<GetSynthesisMilestonesResult> {
    try {
      console.log('🎯 [gameAPI] Getting synthesis milestones...');

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return {
          success: false,
          error: 'User not authenticated'
        };
      }

      const [milestonesResult, progressionResult, unlockedResult] = await Promise.all([
        supabase
          .schema('bubble_game').from('synthesis_milestones')
          .select('*')
          .order('sort_order', { ascending: true }),
        supabase.rpc('get_synthesis_progress', { p_user_id: user.id }),
        supabase
          .schema('bubble_game').from('player_synthesis_milestones')
          .select('*')
          .eq('user_id', user.id)
          .order('unlocked_at', { ascending: false })
      ]);

      if (milestonesResult.error) {
        console.error('❌ [gameAPI] Error getting milestones:', milestonesResult.error);
        return {
          success: false,
          error: milestonesResult.error.message
        };
      }

      if (progressionResult.error) {
        console.error('❌ [gameAPI] Error getting synthesis progression:', progressionResult.error);
        return {
          success: false,
          error: progressionResult.error.message
        };
      }

      const progression = progressionResult.data as {
        total_synthesis_count?: number;
        synthesis_milestone_bonus?: number;
        global_decay_reduction?: number;
        faction_quest_bonus?: number;
        error?: string;
      } | null;

      if (!progression || typeof progression.error === 'string') {
        return {
          success: false,
          error: progression?.error || 'Synthesis progression not found'
        };
      }

      console.log('✅ [gameAPI] Milestones retrieved:', {
        total: milestonesResult.data?.length || 0,
        unlocked: unlockedResult.data?.length || 0,
        synthesis_count: progression.total_synthesis_count || 0
      });

      return {
        success: true,
        progress: {
          total_synthesis_count: progression.total_synthesis_count || 0,
          synthesis_milestone_bonus: progression.synthesis_milestone_bonus || 0,
          global_decay_reduction: progression.global_decay_reduction || 0,
          faction_quest_bonus: progression.faction_quest_bonus || 0,
          milestones: milestonesResult.data || [],
          unlocked_milestones: unlockedResult.data || []
        }
      };
    } catch (error: any) {
      console.error('❌ [gameAPI] getSynthesisMilestones failed:', error);
      return {
        success: false,
        error: error?.message || 'Unknown error occurred'
      };
    }
  },

  // =============================================
  // 皮膚管理 API
  // =============================================

  async getPlayerSkins(layerName?: string): Promise<GetPlayerSkinsResult> {
    try {
      console.log('🎨 [gameAPI] Getting player skins...', layerName ? `for layer: ${layerName}` : 'all layers');

      const { data, error } = await supabase.rpc('get_player_skins', {
        p_user_id: null,
        p_layer_name: layerName || null
      });

      if (error) {
        console.error('❌ [gameAPI] Error getting player skins:', error);
        return {
          success: false,
          skins: [],
          error: error.message
        };
      }

      console.log('✅ [gameAPI] Player skins retrieved:', data?.length || 0);
      return {
        success: true,
        skins: (data || []) as PlayerSkinWithDetails[]
      };
    } catch (error: any) {
      console.error('❌ [gameAPI] getPlayerSkins failed:', error);
      return {
        success: false,
        skins: [],
        error: error?.message || 'Unknown error occurred'
      };
    }
  },

  async getAvailableSkinsByLayer(layerName: string, characterId: string): Promise<GetAvailableSkinsResult> {
    try {
      console.log('🎨 [gameAPI] Getting available skins for layer:', { layerName, characterId });

      const { data, error } = await supabase.rpc('get_available_skins_by_layer', {
        p_layer_name: layerName,
        p_character_id: characterId
      });

      if (error) {
        console.error('❌ [gameAPI] Error getting available skins:', error);
        return {
          success: false,
          skins: [],
          layer_id: layerName,
          error: error.message
        };
      }

      console.log('✅ [gameAPI] Available skins retrieved:', data?.length || 0);
      return {
        success: true,
        skins: (data || []) as AvailableSkin[],
        layer_id: layerName
      };
    } catch (error: any) {
      console.error('❌ [gameAPI] getAvailableSkinsByLayer failed:', error);
      return {
        success: false,
        skins: [],
        layer_id: layerName,
        error: error?.message || 'Unknown error occurred'
      };
    }
  },

  async changeCharacterLayer(
    characterId: string,
    assetId: string | null,
    layerKey?: string
  ): Promise<ChangeCharacterLayerResult> {
    try {
      console.log('🎨 [gameAPI] Changing character layer...', { characterId, assetId, layerKey });

      const { data, error } = await supabase.rpc('change_character_layer', {
        p_character_id: characterId,
        p_asset_id: assetId,
        p_layer_key: layerKey || null
      });

      if (error) {
        console.error('❌ [gameAPI] Error changing character layer:', error);
        return {
          success: false,
          error: error.message
        };
      }

      if (!data || !data.success) {
        const errorMsg = data?.error || 'Failed to change character layer';
        console.error('❌ [gameAPI] Change layer failed:', errorMsg);
        return {
          success: false,
          error: errorMsg
        };
      }

      console.log('✅ [gameAPI] Character layer changed successfully:', data);
      return data as ChangeCharacterLayerResult;
    } catch (error: any) {
      console.error('❌ [gameAPI] changeCharacterLayer failed:', error);
      return {
        success: false,
        error: error?.message || 'Unknown error occurred'
      };
    }
  },

  async purchaseSkin(
    assetId: string,
    currencyType: 'BBG' | 'BBT' | 'WLD' = 'BBG',
    burnTxHash?: string,
    burnAmount?: number
  ): Promise<PurchaseSkinResult> {
    try {
      console.log('🎨 [gameAPI] Purchasing skin...', {
        assetId,
        currencyType,
        burnTxHash,
        burnAmount
      });

      const { data, error } = await supabase.rpc('purchase_skin', {
        p_asset_id: assetId,
        p_currency_type: currencyType,
        p_burn_tx_hash: burnTxHash || null,
        p_burn_amount: burnAmount || 0
      });

      if (error) {
        console.error('❌ [gameAPI] Error purchasing skin:', error);

        // In-Game Wallet 錯誤處理
        let errorMessage = error.message;
        if (error.message.includes('IN_GAME_WALLET_INSUFFICIENT')) {
          errorMessage = 'In-Game Wallet BBG 餘額不足';
        } else if (error.message.includes('IN_GAME_WALLET_NOT_FOUND')) {
          errorMessage = 'In-Game Wallet 不存在，請先儲值';
        } else if (error.message.includes('IN_GAME_WALLET_ERROR')) {
          errorMessage = 'In-Game Wallet 扣款失敗';
        }

        return {
          success: false,
          error: errorMessage
        };
      }

      const normalizedData = normalizeInGameWalletLedgerResult(data, {
        preserveOnChainHash: currencyType !== 'BBG',
      });

      if (!normalizedData || !normalizedData.success) {
        const errorMsg = normalizedData?.error || normalizedData?.message || 'Failed to purchase skin';
        console.error('❌ [gameAPI] Purchase skin failed:', errorMsg);

        // In-Game Wallet 錯誤處理
        let errorMessage = errorMsg;
        if (errorMsg.includes('IN_GAME_WALLET_INSUFFICIENT')) {
          errorMessage = 'In-Game Wallet BBG 餘額不足';
        } else if (errorMsg.includes('IN_GAME_WALLET_NOT_FOUND')) {
          errorMessage = 'In-Game Wallet 不存在，請先儲值';
        } else if (errorMsg.includes('IN_GAME_WALLET_ERROR')) {
          errorMessage = 'In-Game Wallet 扣款失敗';
        }

        return {
          success: false,
          error: errorMessage,
          ...(normalizedData || {})
        } as PurchaseSkinResult;
      }

      console.log('✅ [gameAPI] Skin purchased successfully:', normalizedData);
      return normalizedData as PurchaseSkinResult & InGameWalletLedgerResult;
    } catch (error: any) {
      console.error('❌ [gameAPI] purchaseSkin failed:', error);

      // In-Game Wallet 錯誤處理
      let errorMessage = error?.message || 'Unknown error occurred';
      if (errorMessage.includes('IN_GAME_WALLET_INSUFFICIENT')) {
        errorMessage = 'In-Game Wallet BBG 餘額不足';
      } else if (errorMessage.includes('IN_GAME_WALLET_NOT_FOUND')) {
        errorMessage = 'In-Game Wallet 不存在，請先儲值';
      } else if (errorMessage.includes('IN_GAME_WALLET_ERROR')) {
        errorMessage = 'In-Game Wallet 扣款失敗';
      }

      return {
        success: false,
        error: errorMessage
      };
    }
  },

  async verifyAndPurchaseSkinWithToken(
    worldTransactionId: string,
    tokenSymbol: 'BBT' | 'WLD',
    assetId: string,
    options?: { receipt_id?: string }
  ): Promise<PurchaseSkinResult> {
    try {
      const result = await this.verifyAndPurchaseWithToken(
        worldTransactionId,
        tokenSymbol,
        'character_asset',
        assetId,
        1,
        options
      );

      return result as PurchaseSkinResult;
    } catch (error: any) {
      console.error('❌ [gameAPI] verifyAndPurchaseSkinWithToken failed:', error);
      return {
        success: false,
        error: error?.message || '外觀購買驗證過程發生錯誤'
      };
    }
  },

  async getAssetLayers(): Promise<LayerType[]> {
    try {
      console.log('🎨 [gameAPI] Getting asset layers...');

      const { data, error } = await supabase
        .schema('bubble_game').from('asset_layers')
        .select('*')
        .order('layer_order', { ascending: true });

      if (error) {
        console.error('❌ [gameAPI] Error getting asset layers:', error);
        return [];
      }

      console.log('✅ [gameAPI] Asset layers retrieved:', data?.length || 0);
      return data || [];
    } catch (error: any) {
      console.error('❌ [gameAPI] getAssetLayers failed:', error);
      return [];
    }
  },

  async grantDefaultSkins(): Promise<{ success: boolean; granted_count?: number; error?: string }> {
    try {
      console.log('🎨 [gameAPI] Granting default skins...');

      const { data, error } = await supabase.rpc('grant_default_skins', {
        p_user_id: null
      });

      if (error) {
        console.error('❌ [gameAPI] Error granting default skins:', error);
        return {
          success: false,
          error: error.message
        };
      }

      if (!data || !data.success) {
        const errorMsg = data?.error || 'Failed to grant default skins';
        console.error('❌ [gameAPI] Grant default skins failed:', errorMsg);
        return {
          success: false,
          error: errorMsg
        };
      }

      console.log('✅ [gameAPI] Default skins granted:', data);
      return data;
    } catch (error: any) {
      console.error('❌ [gameAPI] grantDefaultSkins failed:', error);
      return {
        success: false,
        error: error?.message || 'Unknown error occurred'
      };
    }
  },

  async getShopCharacterAssets(request: GetShopCharacterAssetsRequest = {}): Promise<GetShopCharacterAssetsResult> {
    try {
      console.log('🏪 [gameAPI] Getting shop character assets...', request);

      const {
        layerFilter,
        rarityFilter,
        sortBy = 'shop_display_order',
        sortOrder = 'ASC',
        limit = 100,
        offset = 0
      } = request;

      // 將 'all' 轉換為 null (不篩選)
      const layerParam = layerFilter && layerFilter !== 'all' ? layerFilter : null;

      const { data, error } = await supabase.rpc('get_shop_character_assets', {
        p_layer_filter: layerParam,
        p_rarity_filter: rarityFilter || null,
        p_sort_by: sortBy,
        p_sort_order: sortOrder,
        p_limit: limit,
        p_offset: offset
      });

      if (error) {
        console.error('❌ [gameAPI] Error getting shop character assets:', error);
        return {
          success: false,
          items: [],
          error: error.message
        };
      }

      if (!data || !data.success) {
        const errorMsg = data?.error || 'Failed to get shop character assets';
        console.error('❌ [gameAPI] Get shop character assets failed:', errorMsg);
        return {
          success: false,
          items: [],
          error: errorMsg
        };
      }

      console.log('✅ [gameAPI] Shop character assets retrieved:', data.items?.length || 0);

      return {
        success: true,
        items: (data.items || []) as ShopCharacterAsset[],
        filters: data.filters,
        sorting: data.sorting,
        pagination: data.pagination
      };
    } catch (error: any) {
      console.error('❌ [gameAPI] getShopCharacterAssets failed:', error);
      return {
        success: false,
        items: [],
        error: error?.message || 'Unknown error occurred'
      };
    }
  },

  // ==========================================
  // 挖礦首次BBG獎勵相關API
  // ==========================================

  /**
   * 檢查角色今日首次挖礦資格
   */
  async checkFirstDigEligibility(characterId: string): Promise<FirstDigEligibility> {
    try {
      const { data, error } = await supabase.rpc('check_first_dig_eligibility', {
        p_character_id: characterId
      });

      if (error) {
        console.error('[gameAPI] Error checking first dig eligibility:', error);
        throw error;
      }

      return data as FirstDigEligibility;
    } catch (error) {
      console.error('[gameAPI] checkFirstDigEligibility failed:', error);
      throw error;
    }
  },

  /**
   * 獲取角色的首次挖礦領取歷史
   */
  async getFirstDigClaimHistory(
    characterId?: string,
    limit: number = 30
  ): Promise<MiningFirstDigClaim[]> {
    try {
      let query = supabase
        .schema('bubble_game').from('mining_first_dig_claims')
        .select('*')
        .order('claimed_at', { ascending: false })
        .limit(limit);

      if (characterId) {
        query = query.eq('character_id', characterId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('[gameAPI] Error fetching first dig claim history:', error);
        throw error;
      }

      return data as MiningFirstDigClaim[];
    } catch (error) {
      console.error('[gameAPI] getFirstDigClaimHistory failed:', error);
      throw error;
    }
  },

  /**
   * 獲取今日已領取BBG的角色列表
   */
  async getTodayFirstDigClaims(): Promise<MiningFirstDigClaim[]> {
    try {
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD

      const { data, error } = await supabase
        .schema('bubble_game').from('mining_first_dig_claims')
        .select('*')
        .eq('claim_day_utc', today)
        .order('claimed_at', { ascending: false });

      if (error) {
        console.error('[gameAPI] Error fetching today first dig claims:', error);
        throw error;
      }

      return data as MiningFirstDigClaim[];
    } catch (error) {
      console.error('[gameAPI] getTodayFirstDigClaims failed:', error);
      throw error;
    }
  },

  // =============================================
  // 戰力排行榜相關 API
  // =============================================

  /**
   * 獲取全球或種族戰力排行榜
   * @param raceName 種族名稱 (可選，不提供則返回全球排行)
   * @param limit 限制返回數量 (預設 100)
   * @param offset 偏移量 (用於分頁)
   */
  async getLeaderboard(
    raceName?: string,
    limit: number = 100,
    offset: number = 0
  ): Promise<GetLeaderboardResult> {
    try {
      console.log('[gameAPI] getLeaderboard called:', { raceName, limit, offset });

      // 獲取當前用戶
      const { data: { user } } = await supabase.auth.getUser();

      // 構建查詢
      let query = supabase
        .schema('bubble_game').from('combat_power_leaderboard')
        .select('*', { count: 'exact' });

      // 如果指定種族，則篩選
      if (raceName) {
        query = query.eq('race_name', raceName);
      }

      // 排序並限制
      const { data, error, count } = await query
        .order('total_combat_power', { ascending: false })
        .order('last_calculated', { ascending: true })
        .range(offset, offset + limit - 1);

      if (error) {
        console.error('[gameAPI] Error fetching leaderboard:', error);
        return {
          success: false,
          leaderboard: [],
          total_count: 0,
          error: error.message
        };
      }

      const leaderboard = (data || []) as LeaderboardEntry[];

      // 取得玩家名稱
      const userIds = Array.from(new Set(leaderboard.map(entry => entry.user_id))).filter(Boolean);
      if (userIds.length > 0) {
        const { data: profiles, error: profileError } = await supabase
          .rpc('get_public_profiles', { p_user_ids: userIds });

        if (profileError) {
          console.error('[gameAPI] Error fetching player names:', profileError);
        } else {
          type ProfileInfo = { id: string; username?: string; faction_id?: string | null };
          const profileList = (profiles || []) as ProfileInfo[];
          const profileMap = new Map(profileList.map(profile => [profile.id, profile]));

          const factionIds = Array.from(new Set(profileList.map(profile => profile.faction_id).filter(Boolean))) as string[];
          let factionMap = new Map<string, { id: string; name: string; display_name: string }>();

          if (factionIds.length > 0) {
            const { data: factions, error: factionError } = await supabase
              .schema('bubble_game').from('factions')
              .select('id, name, display_name')
              .in('id', factionIds);

            if (factionError) {
              console.error('[gameAPI] Error fetching factions:', factionError);
            } else {
              factionMap = new Map((factions || []).map(faction => [faction.id, faction]));
            }
          }

          leaderboard.forEach(entry => {
            const profile = profileMap.get(entry.user_id);
            entry.player_name = profile?.username || undefined;

            if (profile?.faction_id) {
              const faction = factionMap.get(profile.faction_id);
              if (faction) {
                entry.faction_id = faction.id;
                entry.faction_name = faction.name;
                entry.faction_display_name = faction.display_name;
              }
            }
          });
        }
      }

      // 如果有用戶，查找用戶排名
      let myRank: number | undefined;
      let myRaceRank: number | undefined;

      if (user) {
        // 查找用戶的排名
        const { data: myChar } = await supabase
          .schema('bubble_game').from('combat_power_leaderboard')
          .select('global_rank, race_rank, race_name')
          .eq('user_id', user.id)
          .maybeSingle();

        if (myChar) {
          myRank = myChar.global_rank;
          // 如果查詢的是種族排行且匹配，則設置種族排名
          if (raceName && myChar.race_name === raceName) {
            myRaceRank = myChar.race_rank;
          } else if (!raceName) {
            myRaceRank = myChar.race_rank;
          }
        }
      }

      console.log('[gameAPI] getLeaderboard success:', {
        count: leaderboard.length,
        total: count,
        myRank,
        myRaceRank
      });

      return {
        success: true,
        leaderboard,
        total_count: count || 0,
        my_rank: myRank,
        my_race_rank: myRaceRank
      };
    } catch (error) {
      console.error('[gameAPI] getLeaderboard failed:', error);
      return {
        success: false,
        leaderboard: [],
        total_count: 0,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  },

  /**
   * 獲取角色戰力詳情
   * @param characterId 角色ID
   */
  async getCombatPowerDetail(characterId: string): Promise<CombatPowerDetailResult> {
    try {
      console.log('[gameAPI] getCombatPowerDetail called:', characterId);

      // 獲取戰力數據
      const { data: combatPower, error: powerError } = await supabase
        .schema('bubble_game').from('character_combat_power')
        .select('*')
        .eq('character_id', characterId)
        .maybeSingle();

      if (powerError) {
        console.error('[gameAPI] Error fetching combat power:', powerError);
        return {
          success: false,
          error: powerError.message
        };
      }

      // 獲取角色詳細信息
      const { data: character, error: charError } = await supabase
        .schema('bubble_game').from('combat_power_leaderboard')
        .select('*')
        .eq('character_id', characterId)
        .maybeSingle();

      if (charError) {
        console.error('[gameAPI] Error fetching character:', charError);
        return {
          success: false,
          error: charError.message
        };
      }

      console.log('[gameAPI] getCombatPowerDetail success');

      return {
        success: true,
        combat_power: combatPower as CombatPower,
        character: character as LeaderboardEntry
      };
    } catch (error) {
      console.error('[gameAPI] getCombatPowerDetail failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  },

  /**
   * 手動重新計算角色戰力 (管理功能)
   * @param characterId 角色ID
   */
  async recalculateCombatPower(characterId: string): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('[gameAPI] recalculateCombatPower called:', characterId);

      const { data, error } = await supabase.rpc('calculate_character_combat_power', {
        p_character_id: characterId
      });

      if (error) {
        console.error('[gameAPI] Error recalculating combat power:', error);
        return {
          success: false,
          error: error.message
        };
      }

      console.log('[gameAPI] recalculateCombatPower success:', data);

      return {
        success: true
      };
    } catch (error) {
      console.error('[gameAPI] recalculateCombatPower failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  },

  // =============================================
  // 玩家市場系統 API
  // =============================================

  /**
   * 獲取市場上架列表
   */
  async getMarketplaceListings(request: GetMarketplaceListingsRequest = {}): Promise<GetMarketplaceListingsResult> {
    try {
      console.log('🛒 [gameAPI] Fetching marketplace listings...', request);

      const { data, error } = await supabase.rpc('get_marketplace_listings', {
        p_rarity_filter: request.rarity || null,
        p_category_id_filter: request.categoryId || null,
        p_min_price: request.minPrice || null,
        p_max_price: request.maxPrice || null,
        p_sort_by: request.sortBy || 'listed_at',
        p_sort_order: request.sortOrder || 'DESC',
        p_limit: request.limit || 50,
        p_offset: request.offset || 0
      });

      if (error) {
        console.error('❌ [gameAPI] Error fetching marketplace listings:', error);
        return {
          success: false,
          listings: [],
          total_count: 0,
          error: error.message
        };
      }

      console.log('✅ [gameAPI] Marketplace listings fetched:', data?.length || 0);

      return {
        success: true,
        listings: (data || []) as MarketplaceListing[],
        total_count: data?.length || 0
      };
    } catch (error) {
      console.error('❌ [gameAPI] getMarketplaceListings failed:', error);
      return {
        success: false,
        listings: [],
        total_count: 0,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  },

  /**
   * 獲取我的市場上架列表
   */
  async getMyMarketplaceListings(request: GetMyListingsRequest = {}): Promise<GetMyListingsResult> {
    try {
      console.log('📦 [gameAPI] Fetching my marketplace listings...', request);

      const { data, error } = await supabase.rpc('get_my_marketplace_listings', {
        p_status_filter: request.status || null
      });

      if (error) {
        console.error('❌ [gameAPI] Error fetching my listings:', error);
        return {
          success: false,
          listings: [],
          error: error.message
        };
      }

      console.log('✅ [gameAPI] My marketplace listings fetched:', data?.length || 0);

      return {
        success: true,
        listings: (data || []) as MarketplaceListing[]
      };
    } catch (error) {
      console.error('❌ [gameAPI] getMyMarketplaceListings failed:', error);
      return {
        success: false,
        listings: [],
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  },

  /**
   * 驗證上架資格
   */
  async validateListingEligibility(
    playerItemId: string,
    quantity: number,
    priceWld: number
  ): Promise<ValidateListingResult> {
    try {
      console.log('✔️ [gameAPI] Validating listing eligibility...', {
        playerItemId,
        quantity,
        priceWld
      });

      const { data, error } = await supabase.rpc('validate_marketplace_listing', {
        p_player_item_id: playerItemId,
        p_quantity: quantity,
        p_price_wld: priceWld
      });

      if (error) {
        console.error('❌ [gameAPI] Error validating listing:', error);
        return {
          valid: false,
          error: error.message
        };
      }

      console.log('✅ [gameAPI] Listing validation result:', data);
      return data as ValidateListingResult;
    } catch (error) {
      console.error('❌ [gameAPI] validateListingEligibility failed:', error);
      return {
        valid: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  },

  /**
   * 上架道具到市場（呼叫 Edge Function）
   */
  async listItemOnMarketplace(request: ListItemRequest): Promise<ListItemResult> {
    try {
      console.log('📤 [gameAPI] Listing item on marketplace...', request);

      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData.session) {
        throw new Error('Session expired, please login again');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/marketplace-list-item`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${sessionData.session.access_token}`
          },
          body: JSON.stringify(request)
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('❌ [gameAPI] List item failed:', errorData);
        return {
          success: false,
          error: errorData.error || errorData.details || 'Failed to list item'
        };
      }

      const result = await response.json();
      console.log('✅ [gameAPI] Item listed successfully:', result);

      return {
        success: result.success,
        listing_id: result.listing_id,
        message: result.message,
        error: result.error
      };
    } catch (error) {
      console.error('❌ [gameAPI] listItemOnMarketplace error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  },

  /**
   * 驗證購買資格
   */
  async validatePurchaseEligibility(listingId: string): Promise<ValidatePurchaseResult> {
    try {
      console.log('✔️ [gameAPI] Validating purchase eligibility...', listingId);

      const { data, error } = await supabase.rpc('validate_marketplace_purchase', {
        p_listing_id: listingId
      });

      if (error) {
        console.error('❌ [gameAPI] Error validating purchase:', error);
        return {
          valid: false,
          error: error.message
        };
      }

      console.log('✅ [gameAPI] Purchase validation result:', data);
      return data as ValidatePurchaseResult;
    } catch (error) {
      console.error('❌ [gameAPI] validatePurchaseEligibility failed:', error);
      return {
        valid: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  },

  /**
   * 購買市場道具（呼叫 Edge Function）
   */
  async purchaseMarketplaceItem(request: PurchaseItemRequest): Promise<PurchaseItemResult> {
    try {
      console.log('💰 [gameAPI] Purchasing marketplace item...', request);

      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData.session) {
        throw new Error('Session expired, please login again');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/marketplace-purchase-item`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${sessionData.session.access_token}`
          },
          body: JSON.stringify(request)
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('❌ [gameAPI] Purchase item failed:', errorData);
        return {
          success: false,
          error: errorData.error || errorData.details || 'Failed to purchase item'
        };
      }

      const result = await response.json();
      console.log('✅ [gameAPI] Item purchased successfully:', result);

      return {
        success: result.success,
        message: result.message,
        error: result.error
      };
    } catch (error) {
      console.error('❌ [gameAPI] purchaseMarketplaceItem error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  },

  /**
   * 取消市場上架（呼叫 Edge Function）
   */
  async cancelMarketplaceListing(request: CancelListingRequest): Promise<CancelListingResult> {
    try {
      console.log('❌ [gameAPI] Cancelling marketplace listing...', request);

      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData.session) {
        throw new Error('Session expired, please login again');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/marketplace-cancel-listing`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${sessionData.session.access_token}`
          },
          body: JSON.stringify(request)
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('❌ [gameAPI] Cancel listing failed:', errorData);
        return {
          success: false,
          error: errorData.error || errorData.details || 'Failed to cancel listing'
        };
      }

      const result = await response.json();
      console.log('✅ [gameAPI] Listing cancelled successfully:', result);

      return {
        success: result.success,
        message: result.message,
        error: result.error
      };
    } catch (error) {
      console.error('❌ [gameAPI] cancelMarketplaceListing error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  },

  /**
   * 獲取市場價格限制
   */
  async getMarketplacePriceLimits(): Promise<MarketplacePriceLimit[]> {
    try {
      console.log('💵 [gameAPI] Fetching marketplace price limits...');

      const { data, error } = await supabase
        .schema('bubble_game').from('marketplace_price_limits')
        .select('*')
        .order('max_price_wld', { ascending: true });

      if (error) {
        console.error('❌ [gameAPI] Error fetching price limits:', error);
        return [];
      }

      console.log('✅ [gameAPI] Price limits fetched:', data?.length || 0);
      return (data || []) as MarketplacePriceLimit[];
    } catch (error) {
      console.error('❌ [gameAPI] getMarketplacePriceLimits failed:', error);
      return [];
    }
  },

  /**
   * 獲取市場配置
   */
  async getMarketplaceConfig(): Promise<Record<string, string>> {
    try {
      console.log('⚙️ [gameAPI] Fetching marketplace config...');

      const { data, error } = await supabase
        .schema('bubble_game').from('marketplace_config')
        .select('key, value');

      if (error) {
        console.error('❌ [gameAPI] Error fetching marketplace config:', error);
        return {};
      }

      console.log('✅ [gameAPI] Marketplace config fetched:', data?.length || 0);

      const config: Record<string, string> = {};
      (data || []).forEach((item: MarketplaceConfig) => {
        config[item.key] = item.value;
      });

      return config;
    } catch (error) {
      console.error('❌ [gameAPI] getMarketplaceConfig failed:', error);
      return {};
    }
  },

  /**
   * 獲取當前王座狀態
   */
  async getThroneStatus(): Promise<ThroneStatus | null> {
    try {
      console.log('👑 [gameAPI] Fetching throne status...');

      const { data, error } = await supabase.rpc('get_throne_status');

      if (error) {
        console.error('❌ [gameAPI] Error fetching throne status:', error);
        return null;
      }

      console.log('✅ [gameAPI] Throne status fetched');
      return data as ThroneStatus;
    } catch (error) {
      console.error('❌ [gameAPI] getThroneStatus failed:', error);
      return null;
    }
  },

  /**
   * 驗證必殺技時機陣列
   */
  async validateUltimateTimings(characterId: string, timings: number[]): Promise<ValidationResult> {
    try {
      console.log('🔍 [gameAPI] Validating ultimate timings:', { characterId, timings });

      const { data, error } = await supabase.rpc('validate_ultimate_timings', {
        p_character_id: characterId,
        p_timings: timings
      });

      if (error) {
        console.error('❌ [gameAPI] Error validating timings:', error);
        return {
          valid: false,
          errors: [error.message],
          cooldown: 6,
          max_uses: 5,
          earliest_timing: 2,
          max_battle_seconds: 30
        };
      }

      console.log('✅ [gameAPI] Validation result:', data);
      return data as ValidationResult;
    } catch (error) {
      console.error('❌ [gameAPI] validateUltimateTimings failed:', error);
      return {
        valid: false,
        errors: [error instanceof Error ? error.message : 'Unknown error'],
        cooldown: 6,
        max_uses: 5,
        earliest_timing: 2,
        max_battle_seconds: 30
      };
    }
  },

  /**
   * 保存戰鬥策略
   */
  async saveBattleStrategy(characterId: string, timings: number[], name?: string): Promise<SaveStrategyResponse> {
    try {
      console.log('💾 [gameAPI] Saving battle strategy:', { characterId, timings, name });

      const { data, error } = await supabase.rpc('save_battle_strategy', {
        p_character_id: characterId,
        p_timings: timings,
        p_strategy_name: name || null
      });

      if (error) {
        console.error('❌ [gameAPI] Error saving strategy:', error);
        return {
          success: false,
          error: error.message
        };
      }

      console.log('✅ [gameAPI] Strategy saved:', data);
      return data as SaveStrategyResponse;
    } catch (error) {
      console.error('❌ [gameAPI] saveBattleStrategy failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  },

  /**
   * 獲取角色當前啟用策略
   */
  async getActiveStrategy(characterId: string): Promise<BattleStrategy | null> {
    try {
      console.log('📋 [gameAPI] Getting active strategy for character:', characterId);

      const { data, error } = await supabase.rpc('get_active_strategy', {
        p_character_id: characterId
      });

      if (error) {
        console.error('❌ [gameAPI] Error getting active strategy:', error);
        return null;
      }

      console.log('✅ [gameAPI] Active strategy:', data);
      return data as BattleStrategy | null;
    } catch (error) {
      console.error('❌ [gameAPI] getActiveStrategy failed:', error);
      return null;
    }
  },

  /**
   * 搶王位挑戰
   */
  async challengeThrone(challengerCharacterId: string, ultimateTimings?: number[]): Promise<ThroneChallengeResponse> {
    try {
      console.log('⚔️ [gameAPI] Challenging throne with character:', challengerCharacterId);
      if (ultimateTimings) {
        console.log('🎯 [gameAPI] Using manual ultimate timings:', ultimateTimings);
      }

      const { data, error } = await supabase.rpc('challenge_throne', {
        p_challenger_character_id: challengerCharacterId,
        p_ultimate_timings: ultimateTimings || null
      });

      if (error) {
        console.error('❌ [gameAPI] Error challenging throne:', error);
        return {
          success: false,
          error: error.message
        };
      }

      console.log('✅ [gameAPI] Throne challenge result:', data);

      // 詳細檢查 battle_events 結構
      if (data && (data as any).battle_result?.battle_events) {
        const battleEvents = (data as any).battle_result.battle_events;
        console.log('🔍 [gameAPI] Battle events structure:', {
          hasPerSecond: !!battleEvents.perSecond,
          perSecondLength: battleEvents.perSecond?.length || 0,
          perSecondType: Array.isArray(battleEvents.perSecond) ? 'array' : typeof battleEvents.perSecond
        });

        // 檢查前三個和最後一個 perSecond 元素
        if (Array.isArray(battleEvents.perSecond) && battleEvents.perSecond.length > 0) {
          const first = battleEvents.perSecond[0];
          const second = battleEvents.perSecond[1];
          const third = battleEvents.perSecond[2];
          const last = battleEvents.perSecond[battleEvents.perSecond.length - 1];

          console.log('🔍 [gameAPI] First perSecond event:', {
            hasT: 't' in first,
            tValue: first.t,
            keys: Object.keys(first),
            full: first
          });

          if (second) {
            console.log('🔍 [gameAPI] Second perSecond event:', {
              hasT: 't' in second,
              tValue: second.t,
              keys: Object.keys(second)
            });
          }

          if (third) {
            console.log('🔍 [gameAPI] Third perSecond event:', {
              hasT: 't' in third,
              tValue: third.t,
              keys: Object.keys(third)
            });
          }

          console.log('🔍 [gameAPI] Last perSecond event:', {
            hasT: 't' in last,
            tValue: last.t,
            keys: Object.keys(last),
            full: last
          });

          // 檢查是否所有事件都有 t 屬性
          const allHaveT = battleEvents.perSecond.every((event: any) => 't' in event && event.t !== undefined);
          const missingTCount = battleEvents.perSecond.filter((event: any) => !('t' in event) || event.t === undefined).length;

          console.log('🔍 [gameAPI] PerSecond validation:', {
            totalEvents: battleEvents.perSecond.length,
            allHaveT,
            missingTCount,
            missingTPercentage: ((missingTCount / battleEvents.perSecond.length) * 100).toFixed(2) + '%'
          });

          if (missingTCount > 0) {
            console.warn('⚠️ [gameAPI] WARNING: Some perSecond events are missing the "t" property!');
            console.warn('⚠️ [gameAPI] This will cause battle replay issues.');
          }
        }
      }

      return data as ThroneChallengeResponse;
    } catch (error) {
      console.error('❌ [gameAPI] challengeThrone failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  },

  /**
   * 王者主動退位
   */
  async abdicateThrone(characterId: string): Promise<ThroneAbdicationResult> {
    try {
      console.log('👋 [gameAPI] Abdicating throne with character:', characterId);

      const { data, error } = await supabase.rpc('abdicate_throne', {
        p_character_id: characterId
      });

      if (error) {
        console.error('❌ [gameAPI] Error abdicating throne:', error);
        return {
          success: false,
          error: error.message
        };
      }

      if (!data) {
        console.warn('⚠️ [gameAPI] No data returned from abdicate_throne');
        return {
          success: false,
          error: '退位失敗，請稍後再試'
        };
      }

      console.log('✅ [gameAPI] Throne abdication result:', data);
      return data as ThroneAbdicationResult;
    } catch (error) {
      console.error('❌ [gameAPI] abdicateThrone failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  },

  /**
   * 獲取王位戰鬥歷史
   */
  async getThroneBattleHistory(
    limit: number = 20,
    offset: number = 0
  ): Promise<ThroneBattleHistoryEntry[]> {
    try {
      console.log('📜 [gameAPI] Fetching throne battle history...', { limit, offset });

      const { data, error } = await supabase.rpc('get_throne_battle_history', {
        p_limit: limit,
        p_offset: offset
      });

      if (error) {
        console.error('❌ [gameAPI] Error fetching throne battle history:', error);
        return [];
      }

      if (!data || !Array.isArray(data.battles)) {
        console.warn('⚠️ [gameAPI] Unexpected throne battle history shape:', data);
        return [];
      }

      console.log('✅ [gameAPI] Throne battle history fetched:', data.battles.length);
      return data.battles as ThroneBattleHistoryEntry[];
    } catch (error) {
      console.error('❌ [gameAPI] getThroneBattleHistory failed:', error);
      return [];
    }
  },

  /**
   * 獲取每日王位排行
   */
  async getThroneDailyRankings(date?: string): Promise<ThroneDailyRankingResult | null> {
    try {
      console.log('🏆 [gameAPI] Fetching throne daily rankings...', { date });

      const params = date ? { p_date: date } : {};
      const { data, error } = await supabase.rpc('get_throne_daily_rankings', params);

      if (error) {
        console.error('❌ [gameAPI] Error fetching throne daily rankings:', error);
        return null;
      }

      if (!data) {
        console.warn('⚠️ [gameAPI] No throne daily rankings returned');
        return null;
      }

      const rankings = (data as ThroneDailyRankingResult)?.rankings || [];
      const rankingDate = (data as ThroneDailyRankingResult).ranking_date || (data as ThroneDailyRankingResult as any).date || '';

      // 補上缺少的道具獎勵資訊，確保可以顯示獎勵 icon
      const rewardItemIds = rankings
        .filter(entry => entry.item_reward_id && !entry.item_reward_info)
        .map(entry => entry.item_reward_id as string);

      let itemMap: Record<string, ItemRewardInfo> = {};

      if (rewardItemIds.length > 0) {
        const { data: items, error: itemsError } = await supabase
          .schema('bubble_game').from('items')
          .select('id, name, display_name, description, image_url, rarity, stat_bonuses, equipable_slot, is_tradeable')
          .in('id', rewardItemIds);

        if (itemsError) {
          console.error('❌ [gameAPI] Error fetching reward items:', itemsError);
        } else {
          itemMap = (items || []).reduce((acc, item) => {
            acc[item.id] = {
              id: item.id,
              name: item.name,
              display_name: item.display_name,
              description: item.description,
              image_url: item.image_url,
              rarity: item.rarity,
              stat_bonuses: item.stat_bonuses,
              equipable_slot: item.equipable_slot,
              is_tradeable: item.is_tradeable
            } as ItemRewardInfo;
            return acc;
          }, {} as Record<string, ItemRewardInfo>);
        }
      }

      const normalizedRankings = rankings.map(entry => {
        if (!entry.item_reward_info && entry.item_reward_id && itemMap[entry.item_reward_id]) {
          return { ...entry, item_reward_info: itemMap[entry.item_reward_id] };
        }
        return entry;
      });

      return {
        ...data,
        ranking_date: rankingDate || undefined,
        date: rankingDate || (data as any).date,
        rankings: normalizedRankings
      } as ThroneDailyRankingResult;
    } catch (error) {
      console.error('❌ [gameAPI] getThroneDailyRankings failed:', error);
      return null;
    }
  },

  /**
   * 獲取昨日王座排行榜
   */
  async getYesterdayThroneRankings(): Promise<ThroneDailyRankingResult | null> {
    try {
      const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000);
      const yesterdayDate = yesterday.toISOString().split('T')[0];
      console.log('🏆 [gameAPI] Fetching yesterday throne rankings:', yesterdayDate);
      return await this.getThroneDailyRankings(yesterdayDate);
    } catch (error) {
      console.error('❌ [gameAPI] getYesterdayThroneRankings failed:', error);
      return null;
    }
  },

  /**
   * 查詢王座排行榜獎勵詳情
   */
  async checkThroneRankingReward(date?: string): Promise<ThroneRewardCheckResult | null> {
    try {
      console.log('🔍 [gameAPI] Checking throne ranking reward...');

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.error('❌ [gameAPI] User not authenticated');
        return null;
      }

      const targetDate = date || new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0];

      const { data, error } = await supabase.rpc('check_can_claim_throne_ranking_reward', {
        p_user_id: user.id,
        p_date: targetDate
      });

      if (error) {
        console.error('❌ [gameAPI] Error checking throne ranking reward:', error);
        return null;
      }

      console.log('✅ [gameAPI] Throne ranking reward check result:', data);
      return data as ThroneRewardCheckResult;
    } catch (error) {
      console.error('❌ [gameAPI] checkThroneRankingReward failed:', error);
      return null;
    }
  },

  /**
   * 領取王座排行榜獎勵
   */
  async claimThroneRankingReward(): Promise<{
    success: boolean;
    message?: string;
    error?: string;
    transactionHash?: string;
    rank?: number;
    reward_amount?: number;
    ranking_date?: string;
    details?: any;
  }> {
    try {
      const { data: result, error } = await supabase.rpc('claim_throne_ranking_reward');

      if (error) {
        console.error('❌ [gameAPI] Throne ranking reward claim failed:', error);
        return {
          success: false,
          error: error.message
        };
      }

      if (!result?.success) {
        return {
          success: false,
          error: result?.error || 'Failed to claim throne ranking reward'
        };
      }

      return {
        success: true,
        rank: result.rank,
        reward_amount: result.reward_amount,
        ranking_date: result.ranking_date
      };
    } catch (error) {
      console.error('❌ [gameAPI] claimThroneRankingReward failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  },

  /**
   * 查詢王座獎勵領取詳情（包含交易哈希）
   */
  async getThroneRewardClaimDetails(
    rankingDate?: string
  ): Promise<ThroneRewardClaimDetails | null> {
    try {
      console.log('🔍 [gameAPI] Fetching throne reward claim details...', { rankingDate });

      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        console.error('❌ [gameAPI] No authenticated user');
        return null;
      }

      const { data, error } = await supabase.rpc('get_throne_reward_claim_details', {
        p_user_id: user.id,
        p_ranking_date: rankingDate || null
      });

      if (error) {
        console.error('❌ [gameAPI] Error fetching throne reward claim details:', error);
        return null;
      }

      if (!data || data.length === 0) {
        console.log('ℹ️ [gameAPI] No throne reward claim details found');
        return null;
      }

      console.log('✅ [gameAPI] Throne reward claim details fetched:', data[0]);
      return data[0] as ThroneRewardClaimDetails;
    } catch (error) {
      console.error('❌ [gameAPI] getThroneRewardClaimDetails failed:', error);
      return null;
    }
  },

  /**
   * 查詢今日體力回復使用次數
   */
  async getDailyEnergyRecoveryCount(): Promise<{
    count: number;
    max: number;
    remaining: number;
  }> {
    try {
      console.log('📊 [gameAPI] Fetching daily energy recovery count...');

      const { data, error } = await supabase.rpc('get_daily_energy_recovery_count');

      if (error) {
        console.error('❌ [gameAPI] Error fetching daily energy recovery count:', error);
        return { count: 0, max: 5, remaining: 5 };
      }

      console.log('✅ [gameAPI] Daily energy recovery count fetched:', data);
      return data as { count: number; max: number; remaining: number };
    } catch (error) {
      console.error('❌ [gameAPI] getDailyEnergyRecoveryCount failed:', error);
      return { count: 0, max: 5, remaining: 5 };
    }
  },

  /**
   * 使用 BBT 回復體力
   * @param burnTxHash 區塊鏈交易雜湊
   * @param burnAmount 銷毀的 BBT 數量（預設 100）
   */
  async recoverEnergyWithBBT(
    burnTxHash: string,
    burnAmount: number = 100
  ): Promise<{
    success: boolean;
    new_energy?: number;
    energy_recovered?: number;
    daily_usage_count?: number;
    error?: string;
  }> {
    try {
      console.log('⚡ [gameAPI] Calling recover_energy_with_bbt RPC...', {
        burnTxHash,
        burnAmount
      });

      const { data, error } = await supabase.rpc('recover_energy_with_bbt', {
        burn_tx_hash: burnTxHash,
        burn_amount: burnAmount
      });

      if (error) {
        console.error('❌ [gameAPI] Error recovering energy with BBT:', error);
        return {
          success: false,
          error: error.message
        };
      }

      console.log('✅ [gameAPI] Energy recovered successfully:', data);

      if (data.success === false) {
        return {
          success: false,
          error: data.error
        };
      }

      return {
        success: true,
        new_energy: data.new_energy,
        energy_recovered: data.energy_recovered,
        daily_usage_count: data.daily_usage_count
      };
    } catch (error) {
      console.error('❌ [gameAPI] recoverEnergyWithBBT failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  },

  async getUserExpiredPendingSessions(): Promise<ExpiredHarvestSession[]> {
    try {
      const { data, error } = await supabase.rpc('get_user_expired_pending_items');
      if (error) throw error;
      return (data as ExpiredHarvestSession[]) || [];
    } catch (error) {
      console.error('❌ [gameAPI] getUserExpiredPendingSessions failed:', error);
      return [];
    }
  },

  async claimExpiredSessionWithBBG(sessionId: string): Promise<ClaimExpiredSessionResult> {
    try {
      const { data, error } = await supabase.rpc('claim_expired_session_with_bbg', {
        p_session_id: sessionId
      });
      if (error) throw error;
      return (data as ClaimExpiredSessionResult) || { success: false, error: 'No data returned' };
    } catch (error) {
      console.error('❌ [gameAPI] claimExpiredSessionWithBBG failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  },

  async cancelExpiredSessionClaim(sessionId: string): Promise<CancelExpiredSessionResult> {
    try {
      const { data, error } = await supabase.rpc('cancel_expired_session_claim', {
        p_session_id: sessionId
      });
      if (error) throw error;
      return (data as CancelExpiredSessionResult) || { success: false, error: 'No data returned' };
    } catch (error) {
      console.error('❌ [gameAPI] cancelExpiredSessionClaim failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  },

  async getStoryChapter(chapterNumber: number): Promise<StoryChapterData | null> {
    try {
      const { data, error } = await supabase.rpc('get_story_chapter_with_scenes', {
        p_chapter_number: chapterNumber
      });
      if (error) throw error;
      if (!data || data.error) return null;
      return data as StoryChapterData;
    } catch (error) {
      console.error('❌ [gameAPI] getStoryChapter failed:', error);
      return null;
    }
  },

  async getStoryChapterCatalog(): Promise<StoryChapterCatalogItem[]> {
    try {
      const { data, error } = await supabase
        .schema('bubble_game').from('story_chapters')
        .select('chapter_number, title_i18n_key, sort_order')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      return (data ?? []) as StoryChapterCatalogItem[];
    } catch (error) {
      console.error('❌ [gameAPI] getStoryChapterCatalog failed:', error);
      return [];
    }
  },

  async getPlayerStoryProgress(chapterNumber: number): Promise<PlayerStoryProgress | null> {
    try {
      const { data, error } = await supabase.rpc('get_player_story_progress', {
        p_chapter_number: chapterNumber
      });
      if (error) throw error;
      if (!data || data.error) return null;
      return data as PlayerStoryProgress;
    } catch (error) {
      console.error('❌ [gameAPI] getPlayerStoryProgress failed:', error);
      return null;
    }
  },

  async advanceStoryScene(chapterNumber: number, sceneNumber: number): Promise<AdvanceStorySceneResult> {
    try {
      const { data, error } = await supabase.rpc('advance_story_scene', {
        p_chapter_number: chapterNumber,
        p_scene_number: sceneNumber
      });
      if (error) throw error;
      return (data as AdvanceStorySceneResult) || { success: false, next_scene_number: null, chapter_completed: false, current_scene_number: sceneNumber };
    } catch (error) {
      console.error('❌ [gameAPI] advanceStoryScene failed:', error);
      return {
        success: false,
        next_scene_number: null,
        chapter_completed: false,
        current_scene_number: sceneNumber,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  },

  async submitStoryPuzzleAnswer(
    chapterNumber: number,
    puzzleId: string,
    answer: unknown
  ): Promise<SubmitPuzzleAnswerResult> {
    try {
      const { data, error } = await supabase.rpc('submit_story_puzzle_answer', {
        p_chapter_number: chapterNumber,
        p_puzzle_id: puzzleId,
        p_answer: answer
      });
      if (error) throw error;
      return (data as SubmitPuzzleAnswerResult) || { success: false, is_correct: false, attempt_count: 0, hints_unlocked: 0, already_solved: false };
    } catch (error) {
      console.error('❌ [gameAPI] submitStoryPuzzleAnswer failed:', error);
      return {
        success: false,
        is_correct: false,
        attempt_count: 0,
        hints_unlocked: 0,
        already_solved: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  },

  async claimStoryChapterRewards(chapterNumber: number): Promise<ClaimStoryChapterRewardsResult> {
    try {
      const { data, error } = await supabase.rpc('claim_story_chapter_rewards', {
        p_chapter_number: chapterNumber
      });
      if (error) throw error;
      return (data as ClaimStoryChapterRewardsResult) || { success: false, already_claimed: false, bbg_granted: 0, items_granted: [], skins_granted: [] };
    } catch (error) {
      console.error('❌ [gameAPI] claimStoryChapterRewards failed:', error);
      return {
        success: false,
        already_claimed: false,
        bbg_granted: 0,
        items_granted: [],
        skins_granted: [],
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  },

  async submitStoryTrialAttempt(
    chapterNumber: number,
    trialId: string,
    answer?: unknown
  ): Promise<SubmitTrialAttemptResult> {
    try {
      const { data, error } = await supabase.rpc('submit_story_trial_attempt', {
        p_chapter_number: chapterNumber,
        p_trial_id: trialId,
        p_answer: answer ?? null
      });
      if (error) throw error;
      return (data as SubmitTrialAttemptResult) || {
        success: false, is_correct: false, attempt_count: 0,
        max_attempts: 3, trial_completed: false, already_completed: false
      };
    } catch (error) {
      console.error('❌ [gameAPI] submitStoryTrialAttempt failed:', error);
      return {
        success: false,
        is_correct: false,
        attempt_count: 0,
        max_attempts: 3,
        trial_completed: false,
        already_completed: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  },

  async submitStoryMinigameResult(
    chapterNumber: number,
    minigameId: string,
    success: boolean
  ): Promise<SubmitMinigameResultResult> {
    try {
      const { data, error } = await supabase.rpc('submit_story_minigame_result', {
        p_chapter_number: chapterNumber,
        p_minigame_id: minigameId,
        p_success: success
      });
      if (error) throw error;
      return (data as SubmitMinigameResultResult) || {
        success: false, completed: false, attempts: 0
      };
    } catch (error) {
      console.error('❌ [gameAPI] submitStoryMinigameResult failed:', error);
      return {
        success: false,
        completed: false,
        attempts: 0,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  },

  async getSceneCompletionCount(chapterId: string, sceneNumber: number): Promise<number> {
    try {
      const { data, error } = await supabase.rpc('get_scene_completion_count', {
        p_chapter_id: chapterId,
        p_scene_number: sceneNumber
      });
      if (error) throw error;
      return (data as number) ?? 0;
    } catch (error) {
      console.error('❌ [gameAPI] getSceneCompletionCount failed:', error);
      return 0;
    }
  }
};
